import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { db, functions as firebaseFunctions } from '../firebase-config';
import DashboardAppearanceSettings from './DashboardAppearanceSettings';
import {
  collection, onSnapshot, doc, getDoc, setDoc, updateDoc, query, orderBy, deleteDoc, serverTimestamp, writeBatch, deleteField
} from 'firebase/firestore';

// ─────────────────────────────────────────────────────────────────────────────
// AdminDashboard — Super Admin only (userData.isSuperAdmin === true)
// ─────────────────────────────────────────────────────────────────────────────

const PURPLE = '#7c3aed';
const SIDEBAR_BG = 'linear-gradient(160deg, #1e1b4b 0%, #312e81 100%)';

const FEEDBACK_STATUSES = ['new', 'in_progress', 'testing', 'completed', 'discarded'];
const STATUS_STYLE = {
  new:         { color: '#2563eb', bg: '#eff6ff' },
  in_progress: { color: '#7c3aed', bg: '#f5f3ff' },
  testing:     { color: '#0f766e', bg: '#f0fdfa' },
  completed:   { color: '#16a34a', bg: '#f0fdf4' },
  discarded:   { color: '#94a3b8', bg: '#f8fafc' },
};

/** Dark-mode status chips (readable on slate rows; avoids relying on App.css substring hacks). */
const STATUS_STYLE_DARK = {
  new:         { color: '#93c5fd', bg: '#1e3a8a' },
  in_progress: { color: '#c4b5fd', bg: '#4c1d95' },
  testing:     { color: '#5eead4', bg: '#134e4a' },
  completed:   { color: '#86efac', bg: '#14532d' },
  discarded:   { color: '#cbd5e1', bg: '#1e293b' },
};

function feedbackStatusUi(status, isDark) {
  const map = isDark ? STATUS_STYLE_DARK : STATUS_STYLE;
  return map[status] || map.new;
}

function Toggle({ checked, onChange, danger }) {
  return (
    <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', flexShrink: 0 }}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ opacity: 0, width: 0, height: 0 }} />
      <span style={{
        position: 'absolute', cursor: 'pointer', inset: 0,
        background: checked ? (danger ? '#dc2626' : PURPLE) : '#d1d5db',
        borderRadius: '999px', transition: '0.2s',
      }}>
        <span style={{
          position: 'absolute', top: '3px',
          left: checked ? '23px' : '3px',
          width: '18px', height: '18px',
          background: '#fff', borderRadius: '50%', transition: '0.2s',
        }} />
      </span>
    </label>
  );
}

function StatCard({
  label,
  value,
  accentColor,
  icon,
  onNavigate,
  navigateHint,
  compact,
  isDark = false,
  /** Optional second line under the main value (e.g. feedback breakdown). */
  valueSubline,
}) {
  const clickable = typeof onNavigate === 'function';
  const [hover, setHover] = useState(false);
  const [focusVisible, setFocusVisible] = useState(false);

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onNavigate : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigate();
              }
            }
          : undefined
      }
      onMouseEnter={() => clickable && setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={(e) => clickable && setFocusVisible(e.target.matches(':focus-visible'))}
      onBlur={() => setFocusVisible(false)}
      title={clickable ? navigateHint : undefined}
      aria-label={
        clickable
          ? `${navigateHint || `Open ${label}`}${
              value !== null && value !== undefined && String(value).trim() !== ''
                ? ` (${value})`
                : ''
            }`
          : undefined
      }
      style={{
        background: isDark ? '#111827' : '#fff',
        border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
        borderRadius: '12px',
        padding: compact ? '14px 16px' : '18px 20px',
        borderLeft: `4px solid ${accentColor}`,
        boxShadow: hover && clickable
          ? (isDark ? '0 6px 20px rgba(0,0,0,0.35)' : '0 6px 20px rgba(15,23,42,0.1)')
          : (isDark ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 8px rgba(15,23,42,0.04)'),
        transform: hover && clickable ? 'translateY(-2px)' : 'none',
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
        cursor: clickable ? 'pointer' : 'default',
        outline: focusVisible && clickable ? `2px solid ${accentColor}` : 'none',
        outlineOffset: 2,
      }}
    >
      <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, letterSpacing: '0.05em', lineHeight: 1.35 }}>
        <span style={{ marginRight: '6px' }} aria-hidden>{icon}</span>
        <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{label}</span>
        {clickable && (
          <span style={{ fontWeight: 600, color: '#94a3b8', marginLeft: '6px', letterSpacing: 0 }} aria-hidden>
            →
          </span>
        )}
      </p>
      <p
        style={{
          margin: '8px 0 0',
          fontSize:
            value === '' || (typeof value === 'string' && value.trim() === '')
              ? (compact ? '14px' : '16px')
              : compact
                ? '26px'
                : '32px',
          fontWeight: '800',
          color: isDark ? '#f1f5f9' : '#0f172a',
          minHeight:
            value === '' || (typeof value === 'string' && value.trim() === '')
              ? (compact ? '14px' : '16px')
              : undefined,
        }}
      >
        {value === '' || (typeof value === 'string' && value.trim() === '')
          ? '\u00a0'
          : value ?? '—'}
      </p>
      {valueSubline != null && String(valueSubline).trim() !== '' ? (
        <p
          style={{
            margin: '8px 0 0',
            fontSize: compact ? '11px' : '12px',
            fontWeight: 600,
            color: isDark ? '#94a3b8' : '#64748b',
            lineHeight: 1.4,
            wordBreak: 'break-word',
          }}
        >
          {valueSubline}
        </p>
      ) : null}
    </div>
  );
}

function Card({ children, style, isDark = false, id }) {
  return (
    <div
      id={id}
      style={{
        background: isDark ? '#111827' : '#fff',
        border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
        borderRadius: '12px',
        padding: '24px',
        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.28)' : '0 2px 8px rgba(15,23,42,0.04)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Badge({ children, color = '#7c3aed', bg = '#f5f3ff' }) {
  return (
    <span style={{
      background: bg, color, border: `1px solid ${color}33`,
      borderRadius: '999px', fontSize: '11px', fontWeight: '700',
      padding: '2px 9px', whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function buttonStyle(kind = 'secondary', disabled = false, isDark = false) {
  if (kind === 'secondary') {
    const bg = isDark ? '#1e293b' : '#fff';
    const border = isDark ? '#475569' : '#cbd5e1';
    const text = isDark ? '#e2e8f0' : '#334155';
    return {
      border: `1px solid ${border}`,
      background: disabled ? (isDark ? '#0f172a' : '#f8fafc') : bg,
      color: text,
      borderRadius: '6px',
      fontWeight: 700,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    };
  }
  return {};
}

function SectionTitle({ children, sub, isMobile: narrow, isDark = false }) {
  return (
    <div style={{ marginBottom: narrow ? '18px' : '28px' }}>
      <h1 style={{ margin: 0, fontSize: narrow ? '1.35rem' : '1.75rem', fontWeight: '800', color: isDark ? '#f1f5f9' : '#0f172a', lineHeight: 1.2 }}>{children}</h1>
      {sub && <p style={{ margin: '6px 0 0', color: isDark ? '#94a3b8' : '#64748b', fontSize: narrow ? '13px' : '14px', lineHeight: 1.45 }}>{sub}</p>}
    </div>
  );
}

function formatDateTime(value) {
  const date = value?.toDate?.() || (value instanceof Date ? value : null);
  if (!date) return '—';
  return date.toLocaleString();
}

/** Date on first line, time on second (for compact admin table cells). */
function formatDateTimeStacked(value) {
  const d = value?.toDate?.() || (value instanceof Date ? value : null);
  if (!d) return <span>—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', lineHeight: 1.35 }}>
      <span>{d.toLocaleDateString()}</span>
      <span style={{ fontSize: '12px', opacity: 0.8 }}>{d.toLocaleTimeString()}</span>
    </div>
  );
}

/** Prefer Cloud Function Gemini output; fall back to client-side triage for older docs. */
function feedbackEffectiveClassification(item) {
  const c = item?.aiAnalysis?.classification;
  if (c != null && String(c).trim()) return String(c).toLowerCase().trim();
  return String(item?.triage?.type || item?.normalizedCategory || 'general').toLowerCase();
}

function feedbackEffectivePriority(item) {
  const p = item?.aiAnalysis?.priority;
  if (p != null && String(p).trim()) return String(p).toLowerCase().trim();
  return String(item?.triage?.priority || 'low').toLowerCase();
}

function feedbackAiPending(item) {
  return !item?.aiAnalysis && (item?.aiAnalysisError == null || item?.aiAnalysisError === '');
}

function classificationBadgeColors(cls) {
  switch (cls) {
    case 'defect':
      return { color: '#dc2626', bg: '#fef2f2' };
    case 'enhancement':
    case 'improvement':
      return { color: '#7c3aed', bg: '#f5f3ff' };
    case 'support':
      return { color: '#2563eb', bg: '#eff6ff' };
    case 'discard':
      return { color: '#64748b', bg: '#f8fafc' };
    default:
      return { color: '#0f766e', bg: '#f0fdfa' };
  }
}

function priorityBadgeColors(pri) {
  if (pri === 'high') return { color: '#b45309', bg: '#fffbeb' };
  if (pri === 'medium') return { color: '#1d4ed8', bg: '#eff6ff' };
  return { color: '#475569', bg: '#f8fafc' };
}

const mobileTd = {
  padding: '10px 8px',
  fontSize: '13px',
  color: '#475569',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const mobileTh = {
  padding: '10px 8px',
  fontWeight: '700',
  fontSize: '11px',
  color: '#64748b',
  letterSpacing: '0.04em',
  background: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

function MobileRecordDrawer({ title, onClose, children, isDark = false, fullWidthSheet = true }) {
  const panel = isDark ? '#0f172a' : '#fff';
  const hairline = isDark ? '#334155' : '#e2e8f0';
  const headerBg = isDark ? '#111827' : '#f8fafc';
  const titleColor = isDark ? '#f1f5f9' : '#0f172a';
  const sheetPosition = fullWidthSheet
    ? { left: 0, right: 0, transform: 'none', width: 'auto' }
    : {
        left: '50%',
        right: 'auto',
        width: 'min(560px, calc(100vw - 16px))',
        transform: 'translateX(-50%)',
      };
  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2100 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-mobile-record-title"
        style={{
          position: 'fixed',
          bottom: 0,
          ...sheetPosition,
          maxHeight: '90vh',
          background: panel,
          zIndex: 2101,
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          boxShadow: isDark ? '0 -4px 24px rgba(0,0,0,0.45)' : '0 -4px 24px rgba(15,23,42,0.15)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: `1px solid ${hairline}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            background: headerBg,
          }}
        >
          <div id="admin-mobile-record-title" style={{ fontWeight: '800', fontSize: '16px', color: titleColor, paddingRight: '12px' }}>
            {title}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '22px',
              cursor: 'pointer',
              color: isDark ? '#94a3b8' : '#64748b',
              padding: '4px',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '18px 18px 28px', flex: 1, minHeight: 0, color: isDark ? '#e2e8f0' : undefined }}>
          {children}
        </div>
      </div>
    </>
  );
}

const inputStyle = {
  width: '100%', padding: '9px 12px', border: '1px solid #d1d5db',
  borderRadius: '7px', fontSize: '14px', boxSizing: 'border-box', outline: 'none',
};

function adminInputStyle(isDark) {
  return {
    ...inputStyle,
    background: isDark ? '#0f172a' : '#fff',
    color: isDark ? '#e2e8f0' : '#111827',
    border: `1px solid ${isDark ? '#475569' : '#d1d5db'}`,
    colorScheme: isDark ? 'dark' : 'light',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper Components for Feedback Management
// ─────────────────────────────────────────────────────────────────────────────

function DeleteConfirmDialog({ onConfirm, onCancel, isDark = false }) {
  const panel = isDark ? '#111827' : '#fff';
  const muted = isDark ? '#94a3b8' : '#64748b';
  const heading = isDark ? '#f1f5f9' : '#0f172a';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
      <div style={{ background: panel, borderRadius: '12px', padding: '28px 32px',
                    maxWidth: '380px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: isDark ? '1px solid #334155' : undefined }}>
        <h3 style={{ margin: '0 0 10px', color: heading }}>Delete Feedback?</h3>
        <p style={{ margin: '0 0 24px', color: muted, fontSize: '14px', lineHeight: '1.6' }}>
          This action is permanent and cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel}
            style={{ padding: '9px 20px', border: `1px solid ${isDark ? '#475569' : '#d1d5db'}`, borderRadius: '7px',
                     background: isDark ? '#1e293b' : '#fff', color: isDark ? '#e2e8f0' : '#374151', cursor: 'pointer', fontWeight: '600' }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            style={{ padding: '9px 20px', border: 'none', borderRadius: '7px',
                     background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: '700' }}>
            Yes, Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function FeedbackCard({ item, onView, editingFeedbackId, editingStatus, setEditingStatus, feedbackSaving, saveFeedbackStatus, setEditingFeedbackId, setDeleteConfirmId, isDark = false }) {
  const s = (isDark ? STATUS_STYLE_DARK : STATUS_STYLE)[item.status] || (isDark ? STATUS_STYLE_DARK.new : STATUS_STYLE.new);
  const surface = isDark ? '#111827' : '#fff';
  const hairline = isDark ? '#334155' : '#e2e8f0';
  const heading = isDark ? '#f1f5f9' : '#0f172a';
  const muted = isDark ? '#94a3b8' : '#64748b';
  return (
    <div style={{
      background: surface, border: `1px solid ${hairline}`, borderRadius: '10px',
      padding: '16px', marginBottom: '12px',
      boxShadow: isDark ? '0 1px 4px rgba(0,0,0,0.3)' : '0 1px 4px rgba(15,23,42,0.05)',
      borderLeft: `4px solid ${s.color}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div>
          <div style={{ fontWeight: '700', color: heading, fontSize: '14px' }}>{item.userName || 'Unknown'}</div>
          <div style={{ color: muted, fontSize: '12px' }}>{item.userEmail || '—'}</div>
        </div>
        <Badge children={(item.status || 'new').replace('_', ' ')} color={s.color} bg={s.bg} />
      </div>
      <div style={{ fontWeight: '600', color: heading, fontSize: '13px', marginBottom: '4px' }}>{item.subject || 'No subject'}</div>
      <div style={{ color: muted, fontSize: '12px', lineHeight: '1.5', marginBottom: '10px',
                    overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
        {item.message || '—'}
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px', alignItems: 'center' }}>
        {(() => {
          const cls = feedbackEffectiveClassification(item);
          const pri = feedbackEffectivePriority(item);
          const cc = classificationBadgeColors(cls);
          const pc = priorityBadgeColors(pri);
          return (
            <>
              <Badge children={cls.replace(/_/g, ' ')} color={cc.color} bg={cc.bg} />
              <Badge children={pri} color={pc.color} bg={pc.bg} />
              {feedbackAiPending(item) ? (
                <Badge children="AI pending" color="#ca8a04" bg="#fffbeb" />
              ) : null}
              {item.aiAnalysisError ? (
                <Badge children="AI error" color="#dc2626" bg="#fef2f2" />
              ) : null}
            </>
          );
        })()}
        <Badge children={item.category || 'general'} color="#0f766e" bg="#f0fdfa" />
        <span style={{ color: '#94a3b8', fontSize: '11px', alignSelf: 'center' }}>{formatDateTime(item.createdAt)}</span>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={() => onView(item)}
          style={{ ...buttonStyle('secondary', false, isDark), padding: '6px 14px', fontSize: '12px' }}>View</button>
        <button onClick={() => { setEditingFeedbackId(item.id); setEditingStatus(item.status || 'new'); }}
          style={{
            padding: '6px 14px', fontSize: '12px', fontWeight: '600', borderRadius: '6px', cursor: 'pointer',
            background: isDark ? '#312e81' : '#f5f3ff',
            color: isDark ? '#e9d5ff' : PURPLE,
            border: `1px solid ${isDark ? '#6366f1' : '#ddd6fe'}`,
          }}>Edit Status</button>
        <button onClick={() => setDeleteConfirmId(item.id)}
          style={{
            padding: '6px 14px', fontSize: '12px', fontWeight: '600', borderRadius: '6px', cursor: 'pointer',
            background: isDark ? '#3f1219' : '#fef2f2',
            color: isDark ? '#fecaca' : '#dc2626',
            border: `1px solid ${isDark ? '#7f1d1d' : '#fecaca'}`,
          }}>Delete</button>
      </div>
      {editingFeedbackId === item.id && (
        <div style={{
          marginTop: '12px', padding: '12px',
          background: isDark ? '#0f172a' : '#f8fafc',
          borderRadius: '8px', border: `1px solid ${hairline}`,
        }}>
          <select value={editingStatus} onChange={e => setEditingStatus(e.target.value)}
            style={{ ...adminInputStyle(isDark), width: '100%', padding: '8px', borderRadius: '6px', marginBottom: '8px', fontSize: '14px' }}>
            {FEEDBACK_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/, ' ')}</option>)}
          </select>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => saveFeedbackStatus(item.id, editingStatus)}
              disabled={feedbackSaving}
              style={{ flex: 1, padding: '8px', background: PURPLE, color: '#fff',
                       border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer' }}>
              {feedbackSaving ? 'Saving…' : 'Save Status'}
            </button>
            <button onClick={() => setEditingFeedbackId(null)}
              style={{ ...buttonStyle('secondary', false, isDark), padding: '8px 14px', borderRadius: '6px', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TicketDrawer({ ticket, onClose, resolvedName, onStatusSave, onDelete, onSaveNotes, onSaveWhatHasBeenDone, isDark = false }) {
  const [status, setStatus] = useState(ticket.status || 'new');
  const [notes, setNotes] = useState(ticket.adminNotes || '');
  const [whatDone, setWhatDone] = useState(ticket.whatHasBeenDone || '');
  const [saving, setSaving] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [whatDoneSaved, setWhatDoneSaved] = useState(false);
  const [statusSaved, setStatusSaved] = useState(false);
  const [retryTriageBusy, setRetryTriageBusy] = useState(false);
  const [retryTriageErr, setRetryTriageErr] = useState('');

  React.useEffect(() => {
    setStatus(ticket.status || 'new');
    setNotes(ticket.adminNotes || '');
    setWhatDone(ticket.whatHasBeenDone || '');
  }, [ticket.id, ticket.adminNotes, ticket.status, ticket.whatHasBeenDone]);
  React.useEffect(() => { setRetryTriageErr(''); setStatusSaved(false); }, [ticket.id]);

  const panel = isDark ? '#0f172a' : '#fff';
  const hairline = isDark ? '#334155' : '#e2e8f0';
  const subpanel = isDark ? '#111827' : '#f8fafc';
  const heading = isDark ? '#f1f5f9' : '#0f172a';
  const bodyText = isDark ? '#e2e8f0' : '#334155';
  const muted = isDark ? '#94a3b8' : '#64748b';
  const strongish = isDark ? '#cbd5e1' : '#1e293b';
  const aiWrapBg = isDark ? '#1e1b4b' : '#faf5ff';
  const aiWrapBorder = isDark ? '#4338ca' : '#e9d5ff';
  const okGreen = isDark ? '#86efac' : '#16a34a';

  const handleRetryAiTriage = async () => {
    setRetryTriageErr('');
    setRetryTriageBusy(true);
    try {
      const fn = httpsCallable(firebaseFunctions, 'adminRetryFeedbackAiTriage');
      await fn({ feedbackId: ticket.id });
    } catch (e) {
      setRetryTriageErr(e?.message || 'Could not re-run AI triage.');
    } finally {
      setRetryTriageBusy(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ok = await onStatusSave(ticket.id, status);
      if (ok) {
        setStatusSaved(true);
        setTimeout(() => {
          setStatusSaved(false);
          onClose();
        }, 900);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    await onSaveNotes(ticket.id, notes);
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 2000);
  };

  const handleSaveWhatDone = async () => {
    await onSaveWhatHasBeenDone(ticket.id, whatDone);
    setWhatDoneSaved(true);
    setTimeout(() => setWhatDoneSaved(false), 2000);
  };

  return (
    <>
      <div onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1999 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100vh',
        width: 'min(480px, 100vw)',
        background: panel, zIndex: 2000,
        boxShadow: isDark ? '-4px 0 24px rgba(0,0,0,0.45)' : '-4px 0 24px rgba(15,23,42,0.12)',
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${hairline}`,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      background: subpanel, flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: '800', color: heading, fontSize: '16px' }}>
              {ticket.subject || 'No subject'}
            </div>
            <div style={{ color: muted, fontSize: '12px', marginTop: '2px' }}>
              ID: {ticket.id?.slice(0, 8)}… · {formatDateTime(ticket.createdAt)}
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '20px',
                     cursor: 'pointer', color: muted, padding: '4px' }}>✕</button>
        </div>
        <div style={{ padding: '24px', flex: 1 }}>
          <div style={{ marginBottom: '20px', padding: '14px', background: subpanel,
                        borderRadius: '8px', border: `1px solid ${hairline}` }}>
            <div style={{ fontWeight: '700', color: heading, marginBottom: '6px' }}>Submitted by</div>
            <div style={{ fontWeight: '600', color: strongish }}>{resolvedName}</div>
            <div style={{ color: muted, fontSize: '13px' }}>{ticket.userEmail}</div>
            <div style={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: '12px', marginTop: '4px' }}>
              Role: {ticket.userRole} · UID: {ticket.userId?.slice(0, 12)}…
            </div>
            {ticket.category ? (
              <div style={{ color: muted, fontSize: '12px', marginTop: '6px' }}>
                Form category: <strong style={{ color: isDark ? '#e2e8f0' : '#475569' }}>{ticket.category}</strong>
              </div>
            ) : null}
          </div>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontWeight: '700', color: heading, marginBottom: '10px', fontSize: '13px' }}>AI analysis</div>
            {ticket.aiAnalysis ? (
              <div style={{
                background: aiWrapBg,
                border: `1px solid ${aiWrapBorder}`,
                borderRadius: '8px',
                padding: '12px 14px',
              }}>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  {(() => {
                    const cls = String(ticket.aiAnalysis.classification || 'unknown').toLowerCase();
                    const cc = classificationBadgeColors(cls);
                    return <Badge children={cls.replace(/_/g, ' ')} color={cc.color} bg={cc.bg} />;
                  })()}
                  {(() => {
                    const pri = String(ticket.aiAnalysis.priority || 'low').toLowerCase();
                    const pc = priorityBadgeColors(pri);
                    return <Badge children={pri} color={pc.color} bg={pc.bg} />;
                  })()}
                  {ticket.aiAnalysis.area ? (
                    <Badge children={ticket.aiAnalysis.area} color="#0369a1" bg="#e0f2fe" />
                  ) : null}
                  {ticket.aiAnalysis.model ? (
                    <span style={{ fontSize: '11px', color: muted, alignSelf: 'center' }}>{ticket.aiAnalysis.model}</span>
                  ) : null}
                </div>
                <div style={{ fontSize: '12px', color: muted, marginBottom: '6px' }}>
                  Analyzed: {formatDateTime(ticket.aiAnalysis.analyzedAt)}
                </div>
                <div style={{ fontWeight: '600', color: heading, fontSize: '12px', marginBottom: '4px' }}>Summary</div>
                <div style={{ fontSize: '14px', color: bodyText, lineHeight: 1.55, marginBottom: '12px', whiteSpace: 'pre-wrap' }}>
                  {ticket.aiAnalysis.summary?.trim() || '—'}
                </div>
                <div style={{ fontWeight: '600', color: heading, fontSize: '12px', marginBottom: '4px' }}>Recommendation</div>
                <div style={{ fontSize: '14px', color: bodyText, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                  {ticket.aiAnalysis.recommendation?.trim() || '—'}
                </div>
              </div>
            ) : ticket.aiAnalysisError ? (
              <div style={{
                background: isDark ? '#3f1219' : '#fef2f2',
                border: `1px solid ${isDark ? '#7f1d1d' : '#fecaca'}`,
                borderRadius: '8px',
                padding: '12px 14px',
                fontSize: '13px',
                color: isDark ? '#fecaca' : '#991b1b',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
              }}>
                <strong>AI triage did not complete.</strong>
                <div style={{ marginTop: '8px', fontFamily: 'ui-monospace, monospace', fontSize: '12px' }}>
                  {ticket.aiAnalysisError}
                </div>
                {ticket.aiAnalysisAttemptedAt ? (
                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#b45309' }}>
                    Attempted: {formatDateTime(ticket.aiAnalysisAttemptedAt)}
                  </div>
                ) : null}
              </div>
            ) : (
              <div style={{
                background: isDark ? '#422006' : '#fffbeb',
                border: `1px solid ${isDark ? '#a16207' : '#fde68a'}`,
                borderRadius: '8px',
                padding: '12px 14px',
                fontSize: '13px',
                color: isDark ? '#fde68a' : '#92400e',
                lineHeight: 1.5,
              }}>
                Waiting for AI analysis. If this persists, check that Cloud Functions <code style={{ fontSize: '12px' }}>onFeedbackCreated</code> /{' '}
                <code style={{ fontSize: '12px' }}>processFeedbackAiTriage</code> are deployed and <code style={{ fontSize: '12px' }}>GEMINI_API_KEY</code> is set.
                <div style={{ marginTop: '10px', fontSize: '12px', color: isDark ? '#fcd34d' : '#78350f' }}>
                  A stuck processing lock (e.g. after a function timeout) can block triage until you re-run it or deploy the latest functions with stale-lock recovery.
                </div>
              </div>
            )}
            {(feedbackAiPending(ticket) || ticket.aiAnalysisError) ? (
              <div style={{ marginTop: '12px' }}>
                <button
                  type="button"
                  onClick={handleRetryAiTriage}
                  disabled={retryTriageBusy}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: `1px solid ${isDark ? '#6d28d9' : '#c4b5fd'}`,
                    background: retryTriageBusy
                      ? (isDark ? '#312e81' : '#ede9fe')
                      : (isDark ? '#3730a3' : '#f5f3ff'),
                    color: isDark ? '#e9d5ff' : '#5b21b6',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: retryTriageBusy ? 'not-allowed' : 'pointer',
                  }}
                >
                  {retryTriageBusy ? 'Re-running…' : 'Re-run AI triage'}
                </button>
                {retryTriageErr ? (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: isDark ? '#fca5a5' : '#b91c1c' }}>{retryTriageErr}</div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontWeight: '700', color: heading, marginBottom: '8px', fontSize: '13px' }}>Message</div>
            <div style={{
              whiteSpace: 'pre-wrap', color: bodyText, fontSize: '14px',
              lineHeight: '1.7', background: subpanel, padding: '12px',
              borderRadius: '8px', border: `1px solid ${hairline}`,
            }}>
              {ticket.message || '—'}
            </div>
          </div>

          {/* Screenshot Section */}
          {ticket.screenshot && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontWeight: '700', color: heading, marginBottom: '8px', fontSize: '13px' }}>
                📸 Page Screenshot
              </div>
              <div style={{
                background: subpanel,
                border: `1px solid ${hairline}`,
                borderRadius: '8px',
                padding: '12px',
              }}>
                <img
                  src={ticket.screenshot}
                  alt="Page screenshot"
                  style={{
                    display: 'block',
                    width: '100%',
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '6px',
                    border: `1px solid ${isDark ? '#475569' : '#d1d5db'}`,
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    // Open screenshot in new tab for full view
                    window.open(ticket.screenshot, '_blank');
                  }}
                />
                <div style={{
                  marginTop: '8px',
                  fontSize: '12px',
                  color: muted,
                  textAlign: 'center',
                }}>
                  Click image to view full size
                </div>
              </div>
            </div>
          )}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontWeight: '700', color: heading, marginBottom: '8px', fontSize: '13px' }}>Update Status</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <select value={status} onChange={e => setStatus(e.target.value)}
                style={{ ...adminInputStyle(isDark), flex: 1, padding: '8px 12px', borderRadius: '7px', fontSize: '14px' }}>
                {FEEDBACK_STATUSES.map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace(/_/, ' ')}</option>
                ))}
              </select>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '8px 18px', background: saving ? '#9ca3af' : PURPLE,
                         color: '#fff', border: 'none', borderRadius: '7px',
                         fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
            {statusSaved ? (
              <div style={{ fontSize: '12px', color: okGreen, marginTop: '8px', fontWeight: '600' }}>✓ Status saved</div>
            ) : null}
          </div>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontWeight: '700', color: heading, marginBottom: '8px', fontSize: '13px' }}>Admin Notes</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} onBlur={handleSaveNotes}
              placeholder="Add any notes about this ticket (auto-saves on blur)…"
              style={{ ...adminInputStyle(isDark), width: '100%', minHeight: '100px', padding: '10px', borderRadius: '7px',
                       fontSize: '13px', fontFamily: 'inherit',
                       resize: 'vertical' }} />
            {notesSaved && <div style={{ fontSize: '12px', color: okGreen, marginTop: '4px' }}>✓ Notes saved</div>}
          </div>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontWeight: '700', color: heading, marginBottom: '8px', fontSize: '13px' }}>What has been done</div>
            <p style={{ margin: '0 0 8px', fontSize: '12px', color: muted, lineHeight: 1.45 }}>
              Implementation / verification log (also updated from CLI:{' '}
              <code style={{ fontSize: '11px' }}>npm run feedback:status -- … --done=&quot;…&quot;</code>).
            </p>
            <textarea
              value={whatDone}
              onChange={(e) => setWhatDone(e.target.value)}
              onBlur={handleSaveWhatDone}
              placeholder="e.g. Shipped in v2.3.0; verified on staging…"
              style={{
                ...adminInputStyle(isDark),
                width: '100%',
                minHeight: '100px',
                padding: '10px',
                borderRadius: '7px',
                fontSize: '13px',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
            {whatDoneSaved ? (
              <div style={{ fontSize: '12px', color: okGreen, marginTop: '4px' }}>✓ Saved</div>
            ) : null}
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${hairline}`, flexShrink: 0, background: subpanel }}>
          <button onClick={() => onDelete(ticket.id)}
            style={{
              width: '100%', padding: '10px',
              background: isDark ? '#3f1219' : '#fef2f2',
              color: isDark ? '#fca5a5' : '#dc2626', border: `1px solid ${isDark ? '#7f1d1d' : '#fecaca'}`,
              borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '14px',
            }}>
            Delete this ticket
          </button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function AdminDashboard({ user, userData, onLogout }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // ── Live counts ────────────────────────────────────────────────────────────
  const [ownerCount, setOwnerCount]           = useState(null);
  const [professionalCount, setProfCount]     = useState(null);
  const [realtorCount, setRealtorCount]       = useState(null);

  // ── User lists ─────────────────────────────────────────────────────────────
  const [owners, setOwners]                   = useState([]);
  const [professionals, setProfessionals]     = useState([]);
  const [realtors, setRealtors]               = useState([]);
  const [feedbackItems, setFeedbackItems]     = useState([]);
  const [feedbackError, setFeedbackError]     = useState('');

  // ── Feedback management ────────────────────────────────────────────────────
  const [feedbackSearch, setFeedbackSearch]           = useState('');
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState('all');
  const [editingFeedbackId, setEditingFeedbackId]     = useState(null);
  const [editingStatus, setEditingStatus]             = useState('');
  const [feedbackSaving, setFeedbackSaving]           = useState(false);
  const [selectedTicket, setSelectedTicket]           = useState(null);
  const [deleteConfirmId, setDeleteConfirmId]         = useState(null);
  const [isMobile, setIsMobile]                       = useState(window.innerWidth < 768);
  const [isCompactTable, setIsCompactTable]           = useState(window.innerWidth < 1100);
  const [tenantName, setTenantName]                   = useState('');
  const [statusMenuOpen, setStatusMenuOpen]           = useState(null);
  const [disconnectingStripeFor, setDisconnectingStripeFor] = useState('');
  const [stripeDisconnectMessage, setStripeDisconnectMessage] = useState('');
  const [proDepositWaiverBusyId, setProDepositWaiverBusyId] = useState('');
  const [proDepositWaiverMessage, setProDepositWaiverMessage] = useState('');
  const [mobileRecordDetail, setMobileRecordDetail] = useState(null);

  // ── Multi-select for feedback ───────────────────────────────────────────────
  const [selectedFeedback, setSelectedFeedback]     = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading]   = useState(false);
  const [bulkActionMessage, setBulkActionMessage]   = useState('');

  // ── Platform fee ───────────────────────────────────────────────────────────
  const [feePercent, setFeePercent]           = useState('');
  const [feeSaving, setFeeSaving]             = useState(false);
  const [feeSaved, setFeeSaved]               = useState(false);

  // ── Professional fee overrides (per-professional) ─────────────────────────
  const [proFeeOverrideEnabled, setProFeeOverrideEnabled] = useState(false);
  const [proFeeOverrideValue, setProFeeOverrideValue] = useState('');
  const [proFeeOverrideSaving, setProFeeOverrideSaving] = useState(false);
  const [proFeeOverrideMessage, setProFeeOverrideMessage] = useState('');

  // ── Platform settings ─────────────────────────────────────────────────────
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    allowNewOwnerSignups: true,
    allowNewProfessionalSignups: true,
    allowNewRealtorSignups: true,
    platformName: 'Tenant Dispatch',
    supportEmail: '',
    featurePayments: true,
    featureFeedback: true,
  });
  /** Per-owner max units (OwnersData.maxUnitsPerOwner); blank = no limit */
  const [ownerMaxUnitsInputs, setOwnerMaxUnitsInputs] = useState({});
  const [ownerMaxUnitsSavingId, setOwnerMaxUnitsSavingId] = useState('');
  const [adminUserDeleteBusy, setAdminUserDeleteBusy] = useState('');
  const [settingsSaving, setSettingsSaving]   = useState(false);
  const [settingsSaved, setSettingsSaved]     = useState(false);

  // ── Admin invitation ─────────────────────────────────────────────────────
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');

  // ── Search / filter ────────────────────────────────────────────────────────
  const [ownerSearch, setOwnerSearch]         = useState('');
  const [proSearch, setProSearch]             = useState('');
  const [selectedOwnerIds, setSelectedOwnerIds] = useState(new Set());
  const [selectedProfessionalIds, setSelectedProfessionalIds] = useState(new Set());
  const [selectedRealtorIds, setSelectedRealtorIds] = useState(new Set());
  const [bulkUserDeleteLoading, setBulkUserDeleteLoading] = useState(false);
  const [bulkUserDeleteMessage, setBulkUserDeleteMessage] = useState('');
  const [isDarkTheme, setIsDarkTheme] = useState(() => document.documentElement.classList.contains('theme-dark'));

  // ── Live snapshots ─────────────────────────────────────────────────────────
  useEffect(() => {
    const unsubs = [
      onSnapshot(collection(db, 'OwnersData'),        s => { setOwnerCount(s.size); setOwners(s.docs.map(d => ({ id: d.id, ...d.data() }))); }),
      onSnapshot(collection(db, 'ProfessionalsData'),  s => { setProfCount(s.size); setProfessionals(s.docs.map(d => ({ id: d.id, ...d.data() }))); }),
      onSnapshot(collection(db, 'RealtorsData'),       s => { setRealtorCount(s.size); setRealtors(s.docs.map(d => ({ id: d.id, ...d.data() }))); }),
      onSnapshot(
        query(collection(db, 'feedback'), orderBy('createdAt', 'desc')),
        s => {
          setFeedbackError('');
          setFeedbackItems(s.docs.map(d => ({ id: d.id, ...d.data() })));
        },
        err => {
          console.error('Feedback listener error:', err);
          setFeedbackItems([]);
          if (err?.code === 'permission-denied') {
            setFeedbackError('Permission denied reading feedback. Verify that the signed-in admin has OwnersData/<uid>.isSuperAdmin set to true.');
            return;
          }
          setFeedbackError(err?.message || 'Unable to load feedback right now.');
        }
      ),
    ];
    return () => unsubs.forEach(u => u());
  }, []);

  useEffect(() => {
    setOwnerMaxUnitsInputs((prev) => {
      const next = { ...prev };
      owners.forEach((o) => {
        if (next[o.id] === undefined) {
          next[o.id] =
            o.maxUnitsPerOwner != null && o.maxUnitsPerOwner !== ''
              ? String(o.maxUnitsPerOwner)
              : '';
        }
      });
      Object.keys(next).forEach((id) => {
        if (!owners.some((o) => o.id === id)) delete next[id];
      });
      return next;
    });
  }, [owners]);

  // ── Mobile breakpoint listener ─────────────────────────────────────────────
  useEffect(() => {
    const handler = () => {
      setIsMobile(window.innerWidth < 768);
      setIsCompactTable(window.innerWidth < 1100);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    setMobileRecordDetail(null);
  }, [activeTab]);

  useEffect(() => {
    setProDepositWaiverMessage('');
  }, [mobileRecordDetail?.kind, mobileRecordDetail?.item?.id]);

  // When opening a professional record drawer, initialize fee override UI state.
  useEffect(() => {
    setProFeeOverrideMessage('');
    if (!mobileRecordDetail || mobileRecordDetail.kind !== 'professional') {
      setProFeeOverrideEnabled(false);
      setProFeeOverrideValue('');
      return;
    }

    const p = mobileRecordDetail.item || {};
    const override = typeof p.platformFeeOverridePercentage === 'number' ? p.platformFeeOverridePercentage : null;
    if (override !== null) {
      setProFeeOverrideEnabled(true);
      setProFeeOverrideValue(String(override));
    } else {
      setProFeeOverrideEnabled(false);
      setProFeeOverrideValue('');
    }
  }, [mobileRecordDetail]);

  // ── Platform default fee (PlatformSettings/fees) — live so pro drawer & settings stay in sync
  useEffect(() => {
    const ref = doc(db, 'PlatformSettings', 'fees');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setFeePercent('');
          return;
        }
        const raw = snap.data()?.platformFeePercentage;
        const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? '').trim());
        if (Number.isFinite(n)) {
          setFeePercent(String(n));
        } else {
          setFeePercent('');
        }
      },
      () => setFeePercent('')
    );
    return () => unsub();
  }, []);

  // ── Load platform settings ─────────────────────────────────────────────────
  useEffect(() => {
    getDoc(doc(db, 'adminSettings', 'global')).then((snap) => {
      if (!snap.exists()) return;
      const d = snap.data() || {};
      const { maxUnitsPerOwner: _removed, requireProfessionalOnboardingDeposit: _legacyDepositFlag, ...rest } = d;
      setSettings((prev) => ({ ...prev, ...rest }));
    });
  }, []);

  useEffect(() => {
    setSelectedOwnerIds((prev) => new Set(
      [...prev].filter((id) => owners.some((o) => o.id === id && o.id !== user.uid && o.isSuperAdmin !== true))
    ));
  }, [owners, user.uid]);

  useEffect(() => {
    setSelectedProfessionalIds((prev) => new Set(
      [...prev].filter((id) => professionals.some((p) => p.id === id && p.id !== user.uid))
    ));
  }, [professionals, user.uid]);

  useEffect(() => {
    setSelectedRealtorIds((prev) => new Set(
      [...prev].filter((id) => realtors.some((r) => r.id === id && r.id !== user.uid))
    ));
  }, [realtors, user.uid]);

  useEffect(() => {
    const syncTheme = () => setIsDarkTheme(document.documentElement.classList.contains('theme-dark'));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('storage', syncTheme);
    return () => {
      observer.disconnect();
      window.removeEventListener('storage', syncTheme);
    };
  }, []);

  // ── Save fee ───────────────────────────────────────────────────────────────
  const saveFee = async () => {
    const val = parseFloat(feePercent);
    if (isNaN(val) || val < 0 || val > 100) { alert('Enter a valid percentage between 0 and 100.'); return; }
    setFeeSaving(true);
    try {
      await setDoc(doc(db, 'PlatformSettings', 'fees'), { platformFeePercentage: val }, { merge: true });
      setFeeSaved(true);
      setTimeout(() => setFeeSaved(false), 3000);
    } catch (err) { alert('Error: ' + err.message); }
    setFeeSaving(false);
  };

  // ── Save platform settings ─────────────────────────────────────────────────
  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      const { maxUnitsPerOwner: _drop, ...rest } = settings;
      await setDoc(
        doc(db, 'adminSettings', 'global'),
        {
          ...rest,
          maxUnitsPerOwner: deleteField(),
          requireProfessionalOnboardingDeposit: deleteField(),
        },
        { merge: true }
      );
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (err) { alert('Error: ' + err.message); }
    setSettingsSaving(false);
  };

  // ── Send admin invitation ───────────────────────────────────────────────
  const sendAdminInvite = async () => {
    if (!inviteEmail || !inviteEmail.includes('@')) {
      setInviteMessage('❌ Please enter a valid email address');
      setTimeout(() => setInviteMessage(''), 3000);
      return;
    }

    setInviteLoading(true);
    setInviteMessage('');
    
    try {
      // Call the cloud function to send email invitation
      const { httpsCallable } = await import('firebase/functions');
      const fn = httpsCallable(firebaseFunctions, 'sendAdminInvite');
      
      const result = await fn({
        adminEmail: inviteEmail,
        invitedBy: user.uid,
        invitedByEmail: user.email
      });

      if (result.data.success) {
        setInviteMessage(`✓ Invitation sent to ${inviteEmail}! They should receive an email shortly.`);
        setInviteEmail('');
      } else {
        setInviteMessage(`❌ Failed to send invitation: ${result.data.message}`);
      }
    } catch (err) {
      console.error('Error sending admin invite:', err);
      setInviteMessage(`❌ Error sending invitation: ${err.message || 'Unknown error'}`);
    } finally {
      setInviteLoading(false);
      setTimeout(() => setInviteMessage(''), 5000);
    }
  };

  const feedbackNonCompletedCount = React.useMemo(
    () =>
      feedbackItems.filter((item) => {
        const s = String(item.status || 'new').toLowerCase();
        return s !== 'completed' && s !== 'discarded';
      }).length,
    [feedbackItems]
  );

  const feedbackOpenSummarySubline = React.useMemo(() => {
    const openItems = feedbackItems.filter((item) => {
      const s = String(item.status || 'new').toLowerCase();
      return s !== 'completed' && s !== 'discarded';
    });
    if (openItems.length === 0) return 'All caught up';
    const nNew = openItems.filter((i) => String(i.status || 'new').toLowerCase() === 'new').length;
    const nPipe = openItems.filter((i) =>
      ['in_progress', 'testing'].includes(String(i.status || '').toLowerCase())
    ).length;
    const nAi = openItems.filter((i) => feedbackAiPending(i)).length;
    const bits = [];
    if (nNew) bits.push(`${nNew} new`);
    if (nPipe) bits.push(`${nPipe} in progress/testing`);
    if (nAi) bits.push(`${nAi} AI pending`);
    return bits.length ? bits.join(' · ') : `${openItems.length} open`;
  }, [feedbackItems]);

  // ── Sidebar nav ────────────────────────────────────────────────────────────
  const navItems = [
    { id: 'overview',      icon: '📊', label: 'Overview' },
    { id: 'owners',        icon: '🏠', label: 'Owners',        badge: ownerCount },
    { id: 'professionals', icon: '🔧', label: 'Professionals', badge: professionalCount },
    { id: 'realtors',      icon: '🏡', label: 'Realtors',      badge: realtorCount },
    { id: 'feedback',      icon: '💬', label: 'Feedback',      badge: feedbackNonCompletedCount > 0 ? feedbackNonCompletedCount : null },
    { id: 'settings',      icon: '⚙️', label: 'Platform Settings' },
  ];

  const filteredOwners = owners.filter(o =>
    !ownerSearch ||
    (o.name || '').toLowerCase().includes(ownerSearch.toLowerCase()) ||
    (o.email || '').toLowerCase().includes(ownerSearch.toLowerCase())
  );

  const filteredPros = professionals.filter(p =>
    !proSearch ||
    (p.fullName || '').toLowerCase().includes(proSearch.toLowerCase()) ||
    (p.name || '').toLowerCase().includes(proSearch.toLowerCase()) ||
    (p.businessName || '').toLowerCase().includes(proSearch.toLowerCase()) ||
    (p.email || '').toLowerCase().includes(proSearch.toLowerCase()) ||
    (p.specialty || '').toLowerCase().includes(proSearch.toLowerCase()) ||
    ((Array.isArray(p.categories) ? p.categories.join(' ') : '').toLowerCase().includes(proSearch.toLowerCase()))
  );
  const ownerBulkEligibleIds = filteredOwners
    .filter((o) => o.id !== user.uid && o.isSuperAdmin !== true)
    .map((o) => o.id);
  const professionalBulkEligibleIds = filteredPros
    .filter((p) => p.id !== user.uid)
    .map((p) => p.id);
  const realtorBulkEligibleIds = realtors
    .filter((r) => r.id !== user.uid)
    .map((r) => r.id);

  const getProfessionalDisplayName = (p) =>
    p.fullName || p.name || p.businessName || p.email || '—';

  const getProfessionalCategories = (p) => {
    if (Array.isArray(p.categories) && p.categories.length > 0) return p.categories.filter(Boolean);
    if (p.specialty) return [p.specialty];
    return [];
  };

  /** ProfessionalsData uses `serviceFee` (wizard / profile); legacy docs may have `hourlyRate`. */
  const getProfessionalRateLabel = (p) => {
    const parts = [];
    if (p?.serviceFee != null && String(p.serviceFee).trim() !== '') {
      const n = Number(p.serviceFee);
      if (Number.isFinite(n)) parts.push(`Service fee: $${n}`);
    }
    if (p?.hourlyRate != null && String(p.hourlyRate).trim() !== '') {
      const n = Number(p.hourlyRate);
      if (Number.isFinite(n) && n > 0) parts.push(`Hourly rate: $${n}/hr`);
    }
    return parts.length ? parts.join(' · ') : '—';
  };

  const getProfessionalRowStyle = (i, p, clickable = false) => {
    let bg;
    if (p.stripeConnectId) {
      bg = isDarkTheme
        ? (i % 2 === 0 ? '#052e16' : '#064e3b')
        : (i % 2 === 0 ? '#f0fdf4' : '#ecfdf3');
    } else if (isDarkTheme) {
      bg = i % 2 === 0 ? '#0f172a' : '#111827';
    } else {
      bg = i % 2 === 0 ? '#fff' : '#fafafa';
    }
    return {
      borderBottom: `1px solid ${isDarkTheme ? '#334155' : '#f1f5f9'}`,
      background: bg,
      cursor: clickable ? 'pointer' : 'default',
    };
  };

  const getProfessionalNameStyle = (p) => ({
    fontWeight: '700',
    color: p.stripeConnectId
      ? (isDarkTheme ? '#86efac' : '#166534')
      : (isDarkTheme ? '#f1f5f9' : '#0f172a'),
  });

  // ── Build name lookup map from already-loaded collections ─────────────────
  const userNameMap = React.useMemo(() => {
    const map = {};
    owners.forEach(o => {
      const full = [o.firstName, o.lastName].filter(Boolean).join(' ') || o.name || o.email;
      map[o.id] = full;
    });
    professionals.forEach(p => { map[p.id] = p.fullName || p.name || p.businessName || p.email; });
    realtors.forEach(r => { map[r.id] = r.fullName || r.name || r.email; });
    return map;
  }, [owners, professionals, realtors]);

  // ── Fetch tenant name if needed for drawer ─────────────────────────────────
  useEffect(() => {
    if (selectedTicket?.userRole === 'tenant' && !userNameMap[selectedTicket.userId]) {
      getDoc(doc(db, 'TenantsData', selectedTicket.userId)).then(snap => {
        if (snap.exists()) {
          const data = snap.data();
          setTenantName(data.fullName || data.name || '—');
        }
      }).catch(() => setTenantName('—'));
    }
  }, [selectedTicket?.userId, userNameMap, selectedTicket?.userRole]);

  // ── Filtered feedback by status and search ──────────────────────────────────
  const filteredFeedback = feedbackItems.filter(item => {
    const matchesStatus = feedbackStatusFilter === 'all' || item.status === feedbackStatusFilter;
    const q = feedbackSearch.toLowerCase();
    const ai = item.aiAnalysis || {};
    const matchesSearch = !q ||
      (item.userName || '').toLowerCase().includes(q) ||
      (item.userEmail || '').toLowerCase().includes(q) ||
      (item.subject || '').toLowerCase().includes(q) ||
      (item.message || '').toLowerCase().includes(q) ||
      (item.category || '').toLowerCase().includes(q) ||
      (ai.classification || '').toLowerCase().includes(q) ||
      (ai.area || '').toLowerCase().includes(q) ||
      (ai.summary || '').toLowerCase().includes(q) ||
      (ai.recommendation || '').toLowerCase().includes(q) ||
      String(item.aiAnalysisError || '').toLowerCase().includes(q) ||
      String(item.whatHasBeenDone || '').toLowerCase().includes(q) ||
      String(item.adminNotes || '').toLowerCase().includes(q);
    return matchesStatus && matchesSearch;
  });

  // ── Feedback management functions ──────────────────────────────────────────
  const saveFeedbackStatus = async (id, newStatus) => {
    setFeedbackSaving(true);
    try {
      await updateDoc(doc(db, 'feedback', id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      setEditingFeedbackId(null);
      setStatusMenuOpen(null);
      setFeedbackSaving(false);
      return true;
    } catch (err) {
      alert('Error saving status: ' + err.message);
      setFeedbackSaving(false);
      return false;
    }
  };

  const saveAdminNotes = async (id, notes) => {
    try {
      await updateDoc(doc(db, 'feedback', id), {
        adminNotes: notes,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      alert('Error saving notes: ' + err.message);
    }
  };

  const saveWhatHasBeenDone = async (id, text) => {
    try {
      await updateDoc(doc(db, 'feedback', id), {
        whatHasBeenDone: text,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      alert('Error saving “what has been done”: ' + err.message);
    }
  };

  const deleteFeedback = async (id) => {
    try {
      await deleteDoc(doc(db, 'feedback', id));
      setDeleteConfirmId(null);
      if (selectedTicket?.id === id) setSelectedTicket(null);
    } catch (err) {
      alert('Error deleting feedback: ' + err.message);
    }
  };

  // ── Bulk action functions for feedback ───────────────────────────────────────
  const toggleFeedbackSelection = (id) => {
    const newSelected = new Set(selectedFeedback);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedFeedback(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedFeedback.size === filteredFeedback.length) {
      setSelectedFeedback(new Set());
    } else {
      setSelectedFeedback(new Set(filteredFeedback.map(item => item.id)));
    }
  };

  const bulkDeleteFeedback = async () => {
    if (selectedFeedback.size === 0) return;
    
    const confirmed = window.confirm(
      `Delete ${selectedFeedback.size} feedback item${selectedFeedback.size > 1 ? 's' : ''}?\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    setBulkActionLoading(true);
    setBulkActionMessage('');
    
    try {
      const batch = writeBatch(db);
      selectedFeedback.forEach(id => {
        batch.delete(doc(db, 'feedback', id));
      });
      await batch.commit();
      
      setBulkActionMessage(`✓ Successfully deleted ${selectedFeedback.size} feedback item${selectedFeedback.size > 1 ? 's' : ''}`);
      setSelectedFeedback(new Set());
      
      // Clear selected ticket if it was deleted
      if (selectedTicket && selectedFeedback.has(selectedTicket.id)) {
        setSelectedTicket(null);
      }
    } catch (err) {
      setBulkActionMessage(`❌ Error deleting feedback: ${err.message}`);
    } finally {
      setBulkActionLoading(false);
      setTimeout(() => setBulkActionMessage(''), 5000);
    }
  };

  const bulkUpdateStatus = async (newStatus) => {
    if (selectedFeedback.size === 0) return;

    setBulkActionLoading(true);
    setBulkActionMessage('');
    
    try {
      const batch = writeBatch(db);
      selectedFeedback.forEach(id => {
        batch.update(doc(db, 'feedback', id), {
          status: newStatus,
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();
      
      setBulkActionMessage(`✓ Updated ${selectedFeedback.size} feedback item${selectedFeedback.size > 1 ? 's' : ''} to "${newStatus.replace('_', ' ')}"`);
      setSelectedFeedback(new Set());
    } catch (err) {
      setBulkActionMessage(`❌ Error updating status: ${err.message}`);
    } finally {
      setBulkActionLoading(false);
      setTimeout(() => setBulkActionMessage(''), 5000);
    }
  };

  const saveOwnerMaxUnits = async (ownerId) => {
    const raw = String(ownerMaxUnitsInputs[ownerId] ?? '').trim();
    let payload;
    if (raw === '') {
      payload = { maxUnitsPerOwner: deleteField() };
    } else {
      const n = parseInt(raw, 10);
      if (!Number.isFinite(n) || n < 1) {
        alert('Max units must be a whole number ≥ 1, or leave blank for no limit.');
        return;
      }
      payload = { maxUnitsPerOwner: n };
    }
    setOwnerMaxUnitsSavingId(ownerId);
    try {
      await updateDoc(doc(db, 'OwnersData', ownerId), payload);
    } catch (err) {
      alert(err?.message || 'Could not save max units.');
    } finally {
      setOwnerMaxUnitsSavingId('');
    }
  };

  const handleAdminDeleteUser = async (userId, profileCollection, displayLabel) => {
    const confirmed = window.confirm(
      `Permanently delete ${displayLabel}?\n\n` +
        'This removes their Firebase login and their profile document. ' +
        'Related records (units, maintenance requests, etc.) are not removed automatically.'
    );
    if (!confirmed) return;
    setAdminUserDeleteBusy(`${profileCollection}:${userId}`);
    try {
      const fn = httpsCallable(firebaseFunctions, 'adminDeleteUserAccount');
      await fn({ userId, profileCollection });
      setMobileRecordDetail(null);
    } catch (err) {
      alert(err?.message || 'Delete failed.');
    } finally {
      setAdminUserDeleteBusy('');
    }
  };

  const toggleMultiSelectId = (setState, targetId) => {
    setState((prev) => {
      const next = new Set(prev);
      if (next.has(targetId)) next.delete(targetId);
      else next.add(targetId);
      return next;
    });
  };

  const bulkDeleteUsers = async ({ profileCollection, selectedIds, setSelectedIds, label }) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const confirmed = window.confirm(
      `Delete ${ids.length} ${label} account${ids.length > 1 ? 's' : ''}?\n\n` +
      'This permanently removes Firebase Auth users and profile docs.'
    );
    if (!confirmed) return;

    setBulkUserDeleteLoading(true);
    setBulkUserDeleteMessage('');
    try {
      const fn = httpsCallable(firebaseFunctions, 'adminBulkDeleteUsers');
      const result = await fn({ profileCollection, userIds: ids });
      const deleted = Array.isArray(result?.data?.deleted) ? result.data.deleted : [];
      const errors = Array.isArray(result?.data?.errors) ? result.data.errors : [];

      setSelectedIds((prev) => {
        const next = new Set(prev);
        deleted.forEach((id) => next.delete(id));
        return next;
      });

      if (errors.length === 0) {
        setBulkUserDeleteMessage(`✓ Deleted ${deleted.length} ${label} account${deleted.length !== 1 ? 's' : ''}.`);
      } else {
        setBulkUserDeleteMessage(
          `⚠ Deleted ${deleted.length}/${ids.length}. ${errors.length} failed (see Cloud Functions logs for details).`
        );
      }
    } catch (err) {
      setBulkUserDeleteMessage(`❌ Bulk delete failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setBulkUserDeleteLoading(false);
    }
  };

  const bulkSurfaceStyle = {
    marginBottom: '16px',
    padding: isMobile ? '12px 14px' : '12px 16px',
    background: isDarkTheme ? '#0b1220' : '#fff',
    borderColor: isDarkTheme ? '#334155' : '#e2e8f0',
  };

  const bulkCountTextColor = isDarkTheme ? '#cbd5e1' : '#475569';
  const bulkDeleteDisabled = '#9f123955';
  const bulkDeleteEnabledBg = isDarkTheme ? '#7f1d1d66' : '#fff1f2';
  const bulkDeleteEnabledBorder = isDarkTheme ? '#f87171aa' : '#fecaca';
  const bulkDeleteEnabledColor = isDarkTheme ? '#fecaca' : '#b91c1c';
  const bulkMessageColor = bulkUserDeleteMessage.startsWith('✓')
    ? (isDarkTheme ? '#86efac' : '#166534')
    : bulkUserDeleteMessage.startsWith('⚠')
      ? (isDarkTheme ? '#fde68a' : '#92400e')
      : (isDarkTheme ? '#fca5a5' : '#991b1b');

  const handleDisconnectProfessionalStripe = async (professional) => {
    const accountId = professional?.stripeConnectId;
    if (!accountId) return;

    const confirmed = window.confirm(
      `Disconnect Stripe account ${accountId} for ${professional.name || professional.email || professional.id}?\n\n` +
      'This removes the professional link from your Stripe Connect platform and clears the local account linkage.'
    );
    if (!confirmed) return;

    setDisconnectingStripeFor(professional.id);
    setStripeDisconnectMessage('');
    try {
      const { httpsCallable } = await import('firebase/functions');
      const fn = httpsCallable(firebaseFunctions, 'disconnectProfessionalStripe');
      const result = await fn({ professionalId: professional.id });
      const msg = result?.data?.message || 'Stripe account disconnected.';
      setStripeDisconnectMessage(`✓ ${msg}`);
    } catch (err) {
      setStripeDisconnectMessage(`❌ Could not disconnect Stripe: ${err?.message || 'Unknown error'}`);
    } finally {
      setDisconnectingStripeFor('');
      setTimeout(() => setStripeDisconnectMessage(''), 5000);
    }
  };

  const patchProfessionalInDrawer = (professionalId, patch) => {
    setMobileRecordDetail((d) => {
      if (!d || d.kind !== 'professional' || d.item?.id !== professionalId) return d;
      return { ...d, item: { ...d.item, ...patch } };
    });
  };

  const handleGrantProDepositWaiver = async (professional) => {
    if (!professional?.id) return;
    const label = getProfessionalDisplayName(professional) || professional.email || 'this professional';
    if (
      !window.confirm(
        `Waive the $20 onboarding deposit for ${label}?\n\nThey will unlock full job leads without paying through Stripe.`
      )
    ) {
      return;
    }
    setProDepositWaiverBusyId(professional.id);
    setProDepositWaiverMessage('');
    try {
      const fn = httpsCallable(firebaseFunctions, 'adminGrantProfessionalLeadAccess');
      await fn({ professionalId: professional.id });
      patchProfessionalInDrawer(professional.id, { depositPaid: true, adminLeadAccessGranted: true });
      setProDepositWaiverMessage('✓ Deposit requirement waived for this professional.');
    } catch (err) {
      setProDepositWaiverMessage(`❌ ${err?.message || 'Could not waive deposit.'}`);
    } finally {
      setProDepositWaiverBusyId('');
      setTimeout(() => setProDepositWaiverMessage(''), 8000);
    }
  };

  const handleRevokeProDepositWaiver = async (professional) => {
    if (!professional?.id) return;
    const label = getProfessionalDisplayName(professional) || professional.email || 'this professional';
    if (
      !window.confirm(
        `Remove the admin deposit waiver for ${label}?\n\nThey will need to pay the deposit to unlock leads (unless they already paid through Stripe).`
      )
    ) {
      return;
    }
    setProDepositWaiverBusyId(professional.id);
    setProDepositWaiverMessage('');
    try {
      const fn = httpsCallable(firebaseFunctions, 'adminRevokeProfessionalLeadAccessGrant');
      await fn({ professionalId: professional.id });
      patchProfessionalInDrawer(professional.id, { depositPaid: false, adminLeadAccessGranted: false });
      setProDepositWaiverMessage('✓ Admin waiver removed.');
    } catch (err) {
      setProDepositWaiverMessage(`❌ ${err?.message || 'Could not remove waiver.'}`);
    } finally {
      setProDepositWaiverBusyId('');
      setTimeout(() => setProDepositWaiverMessage(''), 8000);
    }
  };

  const handleSaveProfessionalFeeOverride = async () => {
    if (!mobileRecordDetail || mobileRecordDetail.kind !== 'professional') return;
    const p = mobileRecordDetail.item;
    if (!p?.id) return;

    const rawEnabled = !!proFeeOverrideEnabled;
    const val = rawEnabled ? parseFloat(proFeeOverrideValue) : null;

    if (rawEnabled) {
      if (!Number.isFinite(val) || val < 0 || val > 100) {
        setProFeeOverrideMessage('❌ Fee must be a number between 0 and 100.');
        setTimeout(() => setProFeeOverrideMessage(''), 5000);
        return;
      }
    }

    setProFeeOverrideSaving(true);
    setProFeeOverrideMessage('');

    try {
      const { httpsCallable } = await import('firebase/functions');
      const fn = httpsCallable(firebaseFunctions, 'setProfessionalFeeOverride');

      const resp = await fn({
        professionalId: p.id,
        platformFeeOverridePercentage: rawEnabled ? val : null,
      });

      const next = resp?.data?.platformFeeOverridePercentage;
      setMobileRecordDetail((prev) =>
        prev && prev.kind === 'professional'
          ? { ...prev, item: { ...prev.item, platformFeeOverridePercentage: next } }
          : prev
      );
      setProFeeOverrideMessage(`✓ Updated fee to ${next === null ? 'platform default' : `${next}%`}`);
      setTimeout(() => setProFeeOverrideMessage(''), 5000);
    } catch (err) {
      setProFeeOverrideMessage(`❌ ${err?.message || 'Failed to update professional fee.'}`);
      setTimeout(() => setProFeeOverrideMessage(''), 6000);
    } finally {
      setProFeeOverrideSaving(false);
    }
  };

  const resolvedTicketName = selectedTicket
    ? (userNameMap[selectedTicket.userId] || tenantName || selectedTicket.userName || 'Unknown')
    : 'Unknown';

  const navigateToTab = (tabId) => {
    setActiveTab(tabId);
    setMobileMenuOpen(false);
  };

  const tableScrollWrap = {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  };

  const ownerCardSurface = isDarkTheme ? '#111827' : '#fff';
  const ownerCardBorder  = isDarkTheme ? '#334155' : '#e2e8f0';
  const ownerRowEven     = isDarkTheme ? '#0f172a'  : '#fff';
  const ownerRowOdd      = isDarkTheme ? '#111827'  : '#fafafa';
  const ownerRowBorder   = isDarkTheme ? '#334155'  : '#f1f5f9';
  const ownerHeaderBg    = isDarkTheme ? '#1e293b'  : '#f8fafc';
  const ownerHeaderText  = isDarkTheme ? '#cbd5e1'  : '#64748b';
  const ownerBodyText    = isDarkTheme ? '#cbd5e1'  : '#475569';
  const ownerNameText    = isDarkTheme ? '#f1f5f9'  : '#0f172a';
  const ownerEmptyText   = isDarkTheme ? '#94a3b8'  : '#94a3b8';

  const mobileThThemed = {
    ...mobileTh,
    background: ownerHeaderBg,
    color: ownerHeaderText,
    borderBottom: `1px solid ${ownerCardBorder}`,
  };
  const mobileTdThemed = { ...mobileTd, color: ownerBodyText };
  const subtle = isDarkTheme ? '#94a3b8' : '#64748b';
  const headingText = isDarkTheme ? '#f1f5f9' : '#0f172a';
  const labelText = isDarkTheme ? '#cbd5e1' : '#374151';
  const inputThemed = adminInputStyle(isDarkTheme);

  return (
    <div className="dashboard-container admin-dashboard" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* ── Mobile hamburger ─────────────────────────────────────────────── */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setMobileMenuOpen(prev => !prev)}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? '✕' : '☰'}
      </button>

      {/* ── Mobile overlay ───────────────────────────────────────────────── */}
      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'open' : ''}`} style={{
        background: SIDEBAR_BG,
        display: 'flex',
        flexDirection: 'column',
        padding: isMobile
          ? `28px 0 max(28px, calc(20px + env(safe-area-inset-bottom, 0px)))`
          : '28px 0 20px',
        ...(isMobile ? { height: '100dvh', maxHeight: '100dvh', overflow: 'hidden', boxSizing: 'border-box' } : {}),
      }}>
        <div style={{ padding: '0 22px 28px', flexShrink: 0, order: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <span style={{ fontSize: '22px' }}>🛡️</span>
            <span style={{ color: '#fff', fontWeight: '800', fontSize: '17px', letterSpacing: '-0.02em' }}>Admin Panel</span>
          </div>
          <span style={{
            display: 'inline-block', background: 'rgba(167,139,250,0.25)',
            color: '#c4b5fd', fontSize: '11px', fontWeight: '700',
            padding: '2px 8px', borderRadius: '999px', letterSpacing: '0.06em',
          }}>SUPER ADMIN</span>
        </div>

        <nav style={{
          flex: 1,
          minHeight: 0,
          padding: '0 12px',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          order: isMobile ? 3 : 2,
        }}>
          {navItems.map(item => (
            <div
              key={item.id}
              onClick={() => navigateToTab(item.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', marginBottom: '3px',
                background: activeTab === item.id ? 'rgba(255,255,255,0.13)' : 'transparent',
                color: activeTab === item.id ? '#fff' : 'rgba(255,255,255,0.6)',
                fontWeight: activeTab === item.id ? '600' : '400',
                fontSize: '14px', transition: 'all 0.15s',
              }}
            >
              <span style={{ fontSize: '16px', flexShrink: 0 }}>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.badge != null && (
                <span style={{
                  background: 'rgba(255,255,255,0.15)', color: '#fff',
                  borderRadius: '999px', fontSize: '11px', fontWeight: '700',
                  padding: '1px 7px', minWidth: '20px', textAlign: 'center',
                }}>{item.badge}</span>
              )}
            </div>
          ))}
        </nav>

        <div style={{
          padding: '16px',
          paddingBottom: isMobile ? 'max(16px, calc(12px + env(safe-area-inset-bottom, 0px)))' : '16px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          flexShrink: 0,
          order: isMobile ? 2 : 3,
        }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', margin: '0 0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={user?.email}>
            {user?.email}
          </p>
          <button type="button" onClick={() => { setMobileMenuOpen(false); onLogout(); }} style={{
            width: '100%', marginTop: '10px', padding: '12px 10px',
            background: 'rgba(239,68,68,0.2)', color: '#fecaca',
            border: '1px solid rgba(239,68,68,0.35)', borderRadius: '7px',
            cursor: 'pointer', fontSize: '14px', fontWeight: '700',
          }}>
            🚪 Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main ──────────────────────────────────────────────────────────────── */}
      <main
        className="dashboard-content"
        style={{
          overflowY: 'auto',
          overflowX: isMobile ? 'hidden' : 'auto',
          background: isDarkTheme ? '#0b1220' : '#f8fafc',
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <div>
            <SectionTitle isMobile={isMobile} isDark={isDarkTheme} sub="Snapshot of accounts, open feedback, and platform settings (including the service fee). Tap a tile to open that section.">
              Platform Overview
            </SectionTitle>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: isMobile ? '10px' : '14px', marginBottom: isMobile ? '20px' : '28px' }}>
              <StatCard
                label="OWNERS"
                value={ownerCount}
                accentColor="#2563eb"
                icon="🏠"
                compact={isMobile}
                isDark={isDarkTheme}
                onNavigate={() => navigateToTab('owners')}
                navigateHint="Open Owners"
              />
              <StatCard
                label="PROFESSIONALS"
                value={professionalCount}
                accentColor="#6366f1"
                icon="🔧"
                compact={isMobile}
                isDark={isDarkTheme}
                onNavigate={() => navigateToTab('professionals')}
                navigateHint="Open Professionals"
              />
              <StatCard
                label="REALTORS"
                value={realtorCount}
                accentColor="#0891b2"
                icon="🏡"
                compact={isMobile}
                isDark={isDarkTheme}
                onNavigate={() => navigateToTab('realtors')}
                navigateHint="Open Realtors"
              />
              <StatCard
                label="FEEDBACK OPEN"
                value={feedbackNonCompletedCount}
                valueSubline={feedbackOpenSummarySubline}
                accentColor="#ea580c"
                icon="💬"
                compact={isMobile}
                isDark={isDarkTheme}
                onNavigate={() => navigateToTab('feedback')}
                navigateHint="Open Feedback"
              />
              <StatCard
                label="PLATFORM SETTINGS"
                value={(() => {
                  const bits = [];
                  if (settings.maintenanceMode) bits.push('Maint ON');
                  if (feePercent !== '') bits.push(`${feePercent}% fee`);
                  return bits.length ? bits.join(' · ') : '';
                })()}
                accentColor={settings.maintenanceMode ? '#dc2626' : '#7c3aed'}
                icon="⚙️"
                compact={isMobile}
                isDark={isDarkTheme}
                onNavigate={() => navigateToTab('settings')}
                navigateHint="Open Platform Settings"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
              <Card isDark={isDarkTheme}>
                <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: '700', color: headingText }}>💰 Current Platform Fee</h3>
                <p style={{ margin: 0, fontSize: '36px', fontWeight: '800', color: PURPLE, lineHeight: 1.2 }}>
                  {feePercent !== '' ? (
                    `${feePercent}%`
                  ) : (
                    <span style={{ fontSize: '22px', fontWeight: 700, color: subtle }}>Not configured yet</span>
                  )}
                </p>
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: subtle }}>
                  Charged to service professionals on accepted jobs.
                  <span onClick={() => navigateToTab('settings')} style={{ color: PURPLE, cursor: 'pointer', marginLeft: '6px', fontWeight: 600 }}>Edit in settings →</span>
                </p>
              </Card>

              <Card isDark={isDarkTheme}>
                <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: '700', color: headingText }}>🛡️ Admin Account</h3>
                <div style={{
                  display: 'flex',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: '12px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: isMobile ? '100%' : 'auto' }}>
                    <div style={{
                      width: '42px', height: '42px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px',
                      flexShrink: 0,
                    }}>🛡️</div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: '700', color: headingText, fontSize: '14px' }}>
                        {userData?.name || 'Super Admin'}
                      </p>
                      <p style={{ margin: '2px 0 0', color: subtle, fontSize: '12px', wordBreak: 'break-all' }}>{user?.email}</p>
                    </div>
                  </div>
                  <Badge children="● Active" color="#16a34a" bg="#f0fdf4" />
                </div>
              </Card>
            </div>

            {settings.maintenanceMode && (
              <div style={{
                marginTop: '20px',
                background: isDarkTheme ? '#3f1219' : '#fef2f2',
                border: `1px solid ${isDarkTheme ? '#7f1d1d' : '#fecaca'}`,
                borderRadius: '10px',
                padding: isMobile ? '12px 14px' : '14px 18px',
              }}>
                <p style={{ margin: 0, color: isDarkTheme ? '#fecaca' : '#dc2626', fontWeight: '700', fontSize: isMobile ? '13px' : '14px', lineHeight: 1.5, wordBreak: 'break-word' }}>
                  ⚠️ Maintenance mode is currently ON — all non-admin users see a maintenance screen.
                  <span onClick={() => navigateToTab('settings')} style={{ color: isDarkTheme ? '#fecaca' : '#dc2626', cursor: 'pointer', marginLeft: isMobile ? 0 : '8px', marginTop: isMobile ? '8px' : 0, textDecoration: 'underline', display: isMobile ? 'block' : 'inline' }}>Turn off →</span>
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── OWNERS ── */}
        {activeTab === 'owners' && (
          <div>
            <SectionTitle isMobile={isMobile} isDark={isDarkTheme} sub="All registered property owners on the platform.">
              Owners <span style={{ fontSize: '1rem', fontWeight: 400, color: subtle }}>({ownerCount ?? 0})</span>
            </SectionTitle>

            <Card isDark={isDarkTheme} style={{ marginBottom: '20px', padding: isMobile ? '12px 14px' : '14px 20px', background: ownerCardSurface, borderColor: ownerCardBorder }}>
              <input
                type="text"
                placeholder="Search by name or email…"
                value={ownerSearch}
                onChange={e => setOwnerSearch(e.target.value)}
                style={{ ...inputThemed, width: isMobile ? '100%' : '320px', maxWidth: '100%' }}
              />
            </Card>
            <Card isDark={isDarkTheme} style={bulkSurfaceStyle}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: bulkCountTextColor, fontWeight: 600 }}>
                  {selectedOwnerIds.size} selected
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedOwnerIds(new Set(ownerBulkEligibleIds))}
                  disabled={ownerBulkEligibleIds.length === 0}
                  style={{ ...buttonStyle('secondary', ownerBulkEligibleIds.length === 0, isDarkTheme), padding: '6px 10px', fontSize: '12px' }}
                >
                  Select all eligible
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedOwnerIds(new Set())}
                  disabled={selectedOwnerIds.size === 0}
                  style={{ ...buttonStyle('secondary', selectedOwnerIds.size === 0, isDarkTheme), padding: '6px 10px', fontSize: '12px' }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => bulkDeleteUsers({
                    profileCollection: 'OwnersData',
                    selectedIds: selectedOwnerIds,
                    setSelectedIds: setSelectedOwnerIds,
                    label: 'owner',
                  })}
                  disabled={selectedOwnerIds.size === 0 || bulkUserDeleteLoading}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${bulkDeleteEnabledBorder}`,
                    background: selectedOwnerIds.size === 0 || bulkUserDeleteLoading ? bulkDeleteDisabled : bulkDeleteEnabledBg,
                    color: bulkDeleteEnabledColor,
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: selectedOwnerIds.size === 0 || bulkUserDeleteLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {bulkUserDeleteLoading ? 'Deleting...' : 'Delete selected'}
                </button>
              </div>
              {bulkUserDeleteMessage && (
                <p style={{ margin: '10px 0 0', fontSize: '12px', color: bulkMessageColor }}>
                  {bulkUserDeleteMessage}
                </p>
              )}
            </Card>

            {(isMobile || isCompactTable) ? (
              filteredOwners.length === 0 ? (
                <Card isDark={isDarkTheme} style={{ background: ownerCardSurface, borderColor: ownerCardBorder }}><p style={{ margin: 0, textAlign: 'center', color: ownerEmptyText }}>No owners found.</p></Card>
              ) : (
                <>
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: subtle, lineHeight: 1.45 }}>
                    Tap a row for full details.
                  </p>
                  <Card isDark={isDarkTheme} style={{ padding: 0, overflow: 'hidden', background: ownerCardSurface, borderColor: ownerCardBorder }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: '13px' }}>
                      <colgroup>
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '24%' }} />
                        <col style={{ width: '34%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '12%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th style={{ ...mobileTh, textAlign: 'center', padding: '10px 4px', background: ownerHeaderBg, color: ownerHeaderText, borderBottom: `1px solid ${ownerRowBorder}` }} aria-label="Select row">✓</th>
                          <th style={{ ...mobileTh, background: ownerHeaderBg, color: ownerHeaderText, borderBottom: `1px solid ${ownerRowBorder}` }}>NAME</th>
                          <th style={{ ...mobileTh, background: ownerHeaderBg, color: ownerHeaderText, borderBottom: `1px solid ${ownerRowBorder}` }}>EMAIL</th>
                          <th style={{ ...mobileTh, background: ownerHeaderBg, color: ownerHeaderText, borderBottom: `1px solid ${ownerRowBorder}` }}>ADMIN</th>
                          <th style={{ ...mobileTh, textAlign: 'center', padding: '10px 4px', background: ownerHeaderBg, color: ownerHeaderText, borderBottom: `1px solid ${ownerRowBorder}` }} aria-hidden> </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOwners.map((o, i) => (
                          <tr
                            key={o.id}
                            onClick={() => setMobileRecordDetail({ kind: 'owner', item: o })}
                            style={{
                              borderBottom: `1px solid ${ownerRowBorder}`,
                              background: i % 2 === 0 ? ownerRowEven : ownerRowOdd,
                              cursor: 'pointer',
                            }}
                          >
                            <td style={{ ...mobileTd, textAlign: 'center', padding: '8px 4px' }}>
                              <input
                                type="checkbox"
                                checked={selectedOwnerIds.has(o.id)}
                                disabled={o.id === user.uid || o.isSuperAdmin === true}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleMultiSelectId(setSelectedOwnerIds, o.id)}
                              />
                            </td>
                            <td style={{ ...mobileTd, fontWeight: '600', color: ownerNameText }} title={o.name || ''}>{o.name || '—'}</td>
                            <td style={{ ...mobileTd, color: ownerBodyText }} title={o.email || ''}>{o.email || '—'}</td>
                            <td style={{ ...mobileTd, padding: '8px', color: ownerBodyText }}>
                              {o.isSuperAdmin ? (
                                <Badge children="Yes" color={PURPLE} bg="#f5f3ff" />
                              ) : (
                                <span style={{ color: '#94a3b8', fontSize: '12px' }}>No</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'center', color: '#94a3b8', fontSize: '18px', padding: '8px 4px', verticalAlign: 'middle' }}>›</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card>
                </>
              )
            ) : (
            <Card isDark={isDarkTheme} style={{ padding: 0, overflow: 'visible', background: ownerCardSurface, borderColor: ownerCardBorder }}>
              <div style={tableScrollWrap}>
                      <table style={{ width: '100%', minWidth: '1280px', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: ownerHeaderBg, borderBottom: `1px solid ${ownerCardBorder}` }}>
                    {['Select', 'Name', 'Email', 'Phone', 'Date Created', 'Super Admin', 'Max units', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', fontSize: '12px', color: ownerHeaderText, letterSpacing: '0.04em' }}>
                        {h.toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredOwners.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: '32px', textAlign: 'center', color: ownerEmptyText }}>No owners found.</td></tr>
                  )}
                  {filteredOwners.map((o, i) => (
                    <tr key={o.id} style={{ borderBottom: `1px solid ${ownerRowBorder}`, background: i % 2 === 0 ? ownerRowEven : ownerRowOdd }}>
                      <td style={{ padding: '12px 16px' }}>
                        <input
                          type="checkbox"
                          checked={selectedOwnerIds.has(o.id)}
                          disabled={o.id === user.uid || o.isSuperAdmin === true}
                          onChange={() => toggleMultiSelectId(setSelectedOwnerIds, o.id)}
                        />
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: '600', color: ownerNameText }}>
                        {o.name || '—'}
                        {o.id === user.uid && <Badge children="You" color="#2563eb" bg="#eff6ff" />}
                      </td>
                      <td style={{ padding: '12px 16px', color: ownerBodyText }}>{o.email || '—'}</td>
                      <td style={{ padding: '12px 16px', color: ownerBodyText }}>{o.phone || '—'}</td>
                      <td style={{ padding: '12px 16px', color: ownerBodyText, fontSize: '13px', verticalAlign: 'top' }}>
                        {formatDateTimeStacked(o.createdAt)}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {o.isSuperAdmin
                          ? <Badge children="Super Admin" color={PURPLE} bg="#f5f3ff" />
                          : <span style={{ color: '#94a3b8', fontSize: '13px' }}>No</span>}
                      </td>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <input
                            type="number"
                            min={1}
                            placeholder="∞"
                            value={ownerMaxUnitsInputs[o.id] ?? ''}
                            onChange={(e) => setOwnerMaxUnitsInputs((prev) => ({ ...prev, [o.id]: e.target.value }))}
                            style={{ ...inputThemed, width: '76px', padding: '6px 8px', fontSize: '13px' }}
                          />
                          <button
                            type="button"
                            onClick={() => saveOwnerMaxUnits(o.id)}
                            disabled={ownerMaxUnitsSavingId === o.id}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: `1px solid ${PURPLE}55`,
                              background: '#f5f3ff',
                              color: PURPLE,
                              fontSize: '12px',
                              fontWeight: '700',
                              cursor: ownerMaxUnitsSavingId === o.id ? 'wait' : 'pointer',
                            }}
                          >
                            {ownerMaxUnitsSavingId === o.id ? '…' : 'Save'}
                          </button>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                        <button
                          type="button"
                          onClick={() => handleAdminDeleteUser(o.id, 'OwnersData', o.name || o.email || o.id)}
                          disabled={
                            !!adminUserDeleteBusy ||
                            o.id === user.uid ||
                            o.isSuperAdmin === true
                          }
                          style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid #fecaca',
                            background: '#fff1f2',
                            color: '#b91c1c',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor:
                              adminUserDeleteBusy || o.id === user.uid || o.isSuperAdmin
                                ? 'not-allowed'
                                : 'pointer',
                            opacity: o.id === user.uid || o.isSuperAdmin ? 0.45 : 1,
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </Card>
            )}
          </div>
        )}

        {/* ── PROFESSIONALS ── */}
        {activeTab === 'professionals' && (
          <div>
            <SectionTitle isMobile={isMobile} isDark={isDarkTheme} sub="All service professionals registered on the platform.">
              Professionals <span style={{ fontSize: '1rem', fontWeight: 400, color: subtle }}>({professionalCount ?? 0})</span>
            </SectionTitle>

            {stripeDisconnectMessage && (
              <Card isDark={isDarkTheme} style={{
                marginBottom: '14px',
                borderLeft: `4px solid ${stripeDisconnectMessage.startsWith('✓') ? '#16a34a' : '#dc2626'}`,
                background: stripeDisconnectMessage.startsWith('✓')
                  ? (isDarkTheme ? '#052e16' : '#f0fdf4')
                  : (isDarkTheme ? '#3f1219' : '#fff7f7'),
              }}>
                <p style={{
                  margin: 0,
                  color: stripeDisconnectMessage.startsWith('✓')
                    ? (isDarkTheme ? '#86efac' : '#166534')
                    : (isDarkTheme ? '#fecaca' : '#991b1b'),
                  fontWeight: '700',
                  wordBreak: 'break-word',
                  lineHeight: 1.45,
                }}>
                  {stripeDisconnectMessage}
                </p>
              </Card>
            )}

            <Card isDark={isDarkTheme} style={{ marginBottom: '20px', padding: isMobile ? '12px 14px' : '14px 20px' }}>
              <input
                type="text"
                placeholder="Search by name, email, or specialty…"
                value={proSearch}
                onChange={e => setProSearch(e.target.value)}
                style={{ ...inputThemed, width: isMobile ? '100%' : '360px', maxWidth: '100%' }}
              />
            </Card>
            <Card isDark={isDarkTheme} style={bulkSurfaceStyle}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: bulkCountTextColor, fontWeight: 600 }}>
                  {selectedProfessionalIds.size} selected
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedProfessionalIds(new Set(professionalBulkEligibleIds))}
                  disabled={professionalBulkEligibleIds.length === 0}
                  style={{ ...buttonStyle('secondary', professionalBulkEligibleIds.length === 0, isDarkTheme), padding: '6px 10px', fontSize: '12px' }}
                >
                  Select all eligible
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedProfessionalIds(new Set())}
                  disabled={selectedProfessionalIds.size === 0}
                  style={{ ...buttonStyle('secondary', selectedProfessionalIds.size === 0, isDarkTheme), padding: '6px 10px', fontSize: '12px' }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => bulkDeleteUsers({
                    profileCollection: 'ProfessionalsData',
                    selectedIds: selectedProfessionalIds,
                    setSelectedIds: setSelectedProfessionalIds,
                    label: 'professional',
                  })}
                  disabled={selectedProfessionalIds.size === 0 || bulkUserDeleteLoading}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${bulkDeleteEnabledBorder}`,
                    background: selectedProfessionalIds.size === 0 || bulkUserDeleteLoading ? bulkDeleteDisabled : bulkDeleteEnabledBg,
                    color: bulkDeleteEnabledColor,
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: selectedProfessionalIds.size === 0 || bulkUserDeleteLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {bulkUserDeleteLoading ? 'Deleting...' : 'Delete selected'}
                </button>
              </div>
              {bulkUserDeleteMessage && (
                <p style={{ margin: '10px 0 0', fontSize: '12px', color: bulkMessageColor }}>
                  {bulkUserDeleteMessage}
                </p>
              )}
            </Card>

            {isMobile ? (
              filteredPros.length === 0 ? (
                <Card isDark={isDarkTheme}>
                  <p style={{ margin: 0, textAlign: 'center', color: '#94a3b8' }}>No professionals found.</p>
                </Card>
              ) : (
                <>
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: subtle, lineHeight: 1.45 }}>
                    Tap a row or <strong>›</strong> for full details (payout account, fee override, and more).
                  </p>
                  <Card isDark={isDarkTheme} style={{ padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: '13px' }}>
                      <colgroup>
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '24%' }} />
                        <col style={{ width: '30%' }} />
                        <col style={{ width: '22%' }} />
                        <col style={{ width: '12%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th style={{ ...mobileThThemed, textAlign: 'center', padding: '10px 4px' }} aria-label="Select row">✓</th>
                          <th style={mobileThThemed}>NAME</th>
                          <th style={mobileThThemed}>EMAIL</th>
                          <th style={mobileThThemed}>SPECIALTY</th>
                          <th style={{ ...mobileThThemed, textAlign: 'center', padding: '10px 4px' }} aria-hidden> </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPros.map((p, i) => (
                          <tr
                            key={p.id}
                            onClick={() => setMobileRecordDetail({ kind: 'professional', item: p })}
                            style={getProfessionalRowStyle(i, p, true)}
                          >
                            <td style={{ ...mobileTdThemed, textAlign: 'center', padding: '8px 4px' }}>
                              <input
                                type="checkbox"
                                checked={selectedProfessionalIds.has(p.id)}
                                disabled={p.id === user.uid}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleMultiSelectId(setSelectedProfessionalIds, p.id)}
                              />
                            </td>
                            <td style={{ ...mobileTdThemed, ...getProfessionalNameStyle(p) }} title={getProfessionalDisplayName(p)}>{getProfessionalDisplayName(p)}</td>
                            <td style={mobileTdThemed} title={p.email || ''}>{p.email || '—'}</td>
                            <td style={{ ...mobileTdThemed, padding: '8px' }} title={getProfessionalCategories(p).join(', ')}>
                              {getProfessionalCategories(p).length > 0 ? (
                                <span style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                                  <Badge children={getProfessionalCategories(p)[0]} color="#0f766e" bg="#f0fdfa" />
                                </span>
                              ) : (
                                <span style={{ color: '#94a3b8' }}>—</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'center', color: '#94a3b8', fontSize: '18px', padding: '8px 4px', verticalAlign: 'middle' }}>›</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card>
                </>
              )
            ) : (
            <>
              <p style={{ margin: '0 0 10px', fontSize: '12px', color: subtle, lineHeight: 1.45 }}>
                Press a row or the trailing <strong>›</strong> (not the checkbox or Delete) to manage payout account, fee override, and more.
              </p>
            <Card isDark={isDarkTheme} style={{ padding: 0, overflow: 'visible' }}>
              <div style={tableScrollWrap}>
              <table style={{ width: '100%', minWidth: '1040px', borderCollapse: 'collapse', fontSize: '14px', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '52px' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '17%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '96px' }} />
                  <col style={{ width: '40px' }} />
                </colgroup>
                <thead>
                  <tr style={{ background: ownerHeaderBg, borderBottom: `1px solid ${ownerCardBorder}` }}>
                    {['Select', 'Name', 'Email', 'Specialty', 'Business', 'Date Created', 'Payout account', 'Delete', ''].map((h, hi) => (
                      <th
                        key={`pro-th-${hi}`}
                        scope="col"
                        aria-label={hi === 8 ? 'Press row to open details' : undefined}
                        style={{
                          padding: hi === 0 || hi === 8 ? '12px 10px' : '12px 14px',
                          textAlign: hi === 8 ? 'center' : 'left',
                          fontWeight: '700',
                          fontSize: '12px',
                          color: ownerHeaderText,
                          letterSpacing: '0.04em',
                          whiteSpace: hi === 8 ? 'nowrap' : 'normal',
                        }}
                      >
                        {hi === 8 ? <span style={{ fontSize: '18px', color: ownerHeaderText, lineHeight: 1 }} aria-hidden>›</span> : h.toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredPros.length === 0 && (
                    <tr><td colSpan={9} style={{ padding: '32px', textAlign: 'center', color: ownerEmptyText }}>No professionals found.</td></tr>
                  )}
                  {filteredPros.map((p, i) => (
                    <tr
                      key={p.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setMobileRecordDetail({ kind: 'professional', item: p })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setMobileRecordDetail({ kind: 'professional', item: p });
                        }
                      }}
                      style={getProfessionalRowStyle(i, p, true)}
                      title="Press row for details"
                    >
                      <td style={{ padding: '12px 10px', verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedProfessionalIds.has(p.id)}
                          disabled={p.id === user.uid}
                          onChange={() => toggleMultiSelectId(setSelectedProfessionalIds, p.id)}
                        />
                      </td>
                      <td style={{ padding: '12px 14px', ...getProfessionalNameStyle(p), wordBreak: 'break-word', verticalAlign: 'top' }}>{getProfessionalDisplayName(p)}</td>
                      <td style={{ padding: '12px 14px', color: ownerBodyText, wordBreak: 'break-word' }}>{p.email || '—'}</td>
                      <td style={{ padding: '12px 14px', verticalAlign: 'top', wordBreak: 'break-word' }}>
                        {getProfessionalCategories(p).length > 0 ? (
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '6px',
                              alignItems: 'flex-start',
                              minWidth: 0,
                            }}
                          >
                            {getProfessionalCategories(p).slice(0, 2).map(cat => (
                              <Badge key={`${p.id}-${cat}`} children={cat} color="#0f766e" bg="#f0fdfa" />
                            ))}
                            {getProfessionalCategories(p).length > 2 && (
                              <Badge
                                children={`+${getProfessionalCategories(p).length - 2}`}
                                color="#475569"
                                bg="#f8fafc"
                              />
                            )}
                          </div>
                        ) : <span style={{ color: '#94a3b8' }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 14px', color: ownerBodyText, wordBreak: 'break-word', verticalAlign: 'top' }}>{p.businessName || '—'}</td>
                      <td style={{ padding: '12px 14px', color: ownerBodyText, fontSize: '13px', verticalAlign: 'top' }}>
                        {formatDateTimeStacked(p.createdAt)}
                      </td>
                      <td style={{ padding: '12px 14px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        {p.stripeConnectId ? (
                          <Badge children="Connected" color={isDarkTheme ? '#86efac' : '#166534'} bg={isDarkTheme ? '#052e16' : '#f0fdf4'} />
                        ) : (
                          <Badge children="Not connected" color="#64748b" bg={isDarkTheme ? '#1e293b' : '#f1f5f9'} />
                        )}
                      </td>
                      <td style={{ padding: '12px 10px', verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() =>
                            handleAdminDeleteUser(
                              p.id,
                              'ProfessionalsData',
                              getProfessionalDisplayName(p) || p.email || p.id
                            )
                          }
                          disabled={!!adminUserDeleteBusy || p.id === user.uid}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid #fecaca',
                            background: '#fff1f2',
                            color: '#b91c1c',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: adminUserDeleteBusy || p.id === user.uid ? 'not-allowed' : 'pointer',
                            opacity: p.id === user.uid ? 0.45 : 1,
                          }}
                        >
                          Delete
                        </button>
                      </td>
                      <td
                        style={{
                          textAlign: 'center',
                          color: '#94a3b8',
                          fontSize: '20px',
                          padding: '12px 6px',
                          verticalAlign: 'middle',
                          lineHeight: 1,
                        }}
                        aria-hidden
                      >
                        ›
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </Card>
            </>
            )}
          </div>
        )}

        {/* ── REALTORS ── */}
        {activeTab === 'realtors' && (
          <div>
            <SectionTitle isMobile={isMobile} isDark={isDarkTheme} sub="All realtors registered on the platform.">
              Realtors <span style={{ fontSize: '1rem', fontWeight: 400, color: subtle }}>({realtorCount ?? 0})</span>
            </SectionTitle>
            <Card isDark={isDarkTheme} style={bulkSurfaceStyle}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: bulkCountTextColor, fontWeight: 600 }}>
                  {selectedRealtorIds.size} selected
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedRealtorIds(new Set(realtorBulkEligibleIds))}
                  disabled={realtorBulkEligibleIds.length === 0}
                  style={{ ...buttonStyle('secondary', realtorBulkEligibleIds.length === 0, isDarkTheme), padding: '6px 10px', fontSize: '12px' }}
                >
                  Select all eligible
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedRealtorIds(new Set())}
                  disabled={selectedRealtorIds.size === 0}
                  style={{ ...buttonStyle('secondary', selectedRealtorIds.size === 0, isDarkTheme), padding: '6px 10px', fontSize: '12px' }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => bulkDeleteUsers({
                    profileCollection: 'RealtorsData',
                    selectedIds: selectedRealtorIds,
                    setSelectedIds: setSelectedRealtorIds,
                    label: 'realtor',
                  })}
                  disabled={selectedRealtorIds.size === 0 || bulkUserDeleteLoading}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${bulkDeleteEnabledBorder}`,
                    background: selectedRealtorIds.size === 0 || bulkUserDeleteLoading ? bulkDeleteDisabled : bulkDeleteEnabledBg,
                    color: bulkDeleteEnabledColor,
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: selectedRealtorIds.size === 0 || bulkUserDeleteLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {bulkUserDeleteLoading ? 'Deleting...' : 'Delete selected'}
                </button>
              </div>
              {bulkUserDeleteMessage && (
                <p style={{ margin: '10px 0 0', fontSize: '12px', color: bulkMessageColor }}>
                  {bulkUserDeleteMessage}
                </p>
              )}
            </Card>

            {isMobile ? (
              realtors.length === 0 ? (
                <Card isDark={isDarkTheme}><p style={{ margin: 0, textAlign: 'center', color: '#94a3b8' }}>No realtors found.</p></Card>
              ) : (
                <>
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: subtle, lineHeight: 1.45 }}>
                    Tap a row for full details.
                  </p>
                  <Card isDark={isDarkTheme} style={{ padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: '13px' }}>
                      <colgroup>
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '24%' }} />
                        <col style={{ width: '34%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '12%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th style={{ ...mobileThThemed, textAlign: 'center', padding: '10px 4px' }} aria-label="Select row">✓</th>
                          <th style={mobileThThemed}>NAME</th>
                          <th style={mobileThThemed}>EMAIL</th>
                          <th style={mobileThThemed}>PHONE</th>
                          <th style={{ ...mobileThThemed, textAlign: 'center', padding: '10px 4px' }} aria-hidden> </th>
                        </tr>
                      </thead>
                      <tbody>
                        {realtors.map((r, i) => (
                          <tr
                            key={r.id}
                            onClick={() => setMobileRecordDetail({ kind: 'realtor', item: r })}
                            style={{
                              borderBottom: `1px solid ${ownerRowBorder}`,
                              background: i % 2 === 0 ? ownerRowEven : ownerRowOdd,
                              cursor: 'pointer',
                            }}
                          >
                            <td style={{ ...mobileTdThemed, textAlign: 'center', padding: '8px 4px' }}>
                              <input
                                type="checkbox"
                                checked={selectedRealtorIds.has(r.id)}
                                disabled={r.id === user.uid}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleMultiSelectId(setSelectedRealtorIds, r.id)}
                              />
                            </td>
                            <td style={{ ...mobileTdThemed, fontWeight: '600', color: ownerNameText }} title={r.name || ''}>{r.name || '—'}</td>
                            <td style={mobileTdThemed} title={r.email || ''}>{r.email || '—'}</td>
                            <td style={mobileTdThemed} title={r.phone || ''}>{r.phone || '—'}</td>
                            <td style={{ textAlign: 'center', color: '#94a3b8', fontSize: '18px', padding: '8px 4px', verticalAlign: 'middle' }}>›</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card>
                </>
              )
            ) : (
            <Card isDark={isDarkTheme} style={{ padding: 0, overflow: 'visible' }}>
              <div style={tableScrollWrap}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: ownerHeaderBg, borderBottom: `1px solid ${ownerCardBorder}` }}>
                    {['Select', 'Name', 'Email', 'Phone', 'Date Created', 'License #', 'Agency', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', fontSize: '12px', color: ownerHeaderText, letterSpacing: '0.04em' }}>
                        {h.toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {realtors.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: '32px', textAlign: 'center', color: ownerEmptyText }}>No realtors found.</td></tr>
                  )}
                  {realtors.map((r, i) => (
                    <tr key={r.id} style={{ borderBottom: `1px solid ${ownerRowBorder}`, background: i % 2 === 0 ? ownerRowEven : ownerRowOdd }}>
                      <td style={{ padding: '12px 16px' }}>
                        <input
                          type="checkbox"
                          checked={selectedRealtorIds.has(r.id)}
                          disabled={r.id === user.uid}
                          onChange={() => toggleMultiSelectId(setSelectedRealtorIds, r.id)}
                        />
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: '600', color: ownerNameText }}>{r.name || '—'}</td>
                      <td style={{ padding: '12px 16px', color: ownerBodyText }}>{r.email || '—'}</td>
                      <td style={{ padding: '12px 16px', color: ownerBodyText }}>{r.phone || '—'}</td>
                      <td style={{ padding: '12px 16px', color: ownerBodyText, fontSize: '13px', verticalAlign: 'top' }}>
                        {formatDateTimeStacked(r.createdAt)}
                      </td>
                      <td style={{ padding: '12px 16px', color: ownerBodyText }}>{r.licenseNumber || '—'}</td>
                      <td style={{ padding: '12px 16px', color: ownerBodyText }}>{r.agencyName || r.agency || '—'}</td>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                        <button
                          type="button"
                          onClick={() => handleAdminDeleteUser(r.id, 'RealtorsData', r.name || r.email || r.id)}
                          disabled={!!adminUserDeleteBusy || r.id === user.uid}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid #fecaca',
                            background: '#fff1f2',
                            color: '#b91c1c',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: adminUserDeleteBusy || r.id === user.uid ? 'not-allowed' : 'pointer',
                            opacity: r.id === user.uid ? 0.45 : 1,
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </Card>
            )}
          </div>
        )}

        {/* ── FEEDBACK ── */}
        {activeTab === 'feedback' && (
          <div>
            <SectionTitle isMobile={isMobile} isDark={isDarkTheme} sub="In-app feedback with Gemini AI classification, summary, and recommendations (see each ticket for full analysis).">
              Feedback <span style={{ fontSize: '1rem', fontWeight: 400, color: subtle }}>({feedbackItems.length})</span>
            </SectionTitle>

            <Card isDark={isDarkTheme} style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                <Badge children={`New: ${feedbackItems.filter(item => item.status === 'new').length}`} color="#2563eb" bg="#eff6ff" />
                <Badge
                  children={`Defects: ${feedbackItems.filter(item => feedbackEffectiveClassification(item) === 'defect').length}`}
                  color="#dc2626"
                  bg="#fef2f2"
                />
                <Badge
                  children={`Enhancements: ${feedbackItems.filter(item => ['enhancement', 'improvement'].includes(feedbackEffectiveClassification(item))).length}`}
                  color="#7c3aed"
                  bg="#f5f3ff"
                />
                <Badge
                  children={`High priority: ${feedbackItems.filter(item => feedbackEffectivePriority(item) === 'high').length}`}
                  color="#b45309"
                  bg="#fffbeb"
                />
                <Badge
                  children={`AI pending: ${feedbackItems.filter(item => feedbackAiPending(item)).length}`}
                  color="#ca8a04"
                  bg="#fffbeb"
                />
              </div>
            </Card>

            {feedbackError && (
              <Card isDark={isDarkTheme} style={{
                marginBottom: '20px',
                borderLeft: '4px solid #dc2626',
                background: isDarkTheme ? '#3f1219' : '#fff7f7',
              }}>
                <p style={{ margin: 0, fontWeight: '700', color: isDarkTheme ? '#fecaca' : '#991b1b' }}>Unable to load feedback</p>
                <p style={{ margin: '8px 0 0', color: isDarkTheme ? '#fca5a5' : '#7f1d1d', fontSize: '14px', lineHeight: '1.5' }}>
                  {feedbackError}
                </p>
              </Card>
            )}

            <Card isDark={isDarkTheme} style={{ marginBottom: '16px', padding: '14px 20px' }}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input placeholder="Search feedback..." value={feedbackSearch} onChange={e => setFeedbackSearch(e.target.value)}
                       style={{ ...inputThemed, width: isMobile ? '100%' : '280px', padding: '8px 12px' }} />
                <select value={feedbackStatusFilter} onChange={e => setFeedbackStatusFilter(e.target.value)}
                        style={{ ...inputThemed, width: isMobile ? '100%' : '180px', cursor: 'pointer', padding: '8px 12px' }}>
                  <option value="all">All Statuses</option>
                  {FEEDBACK_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/, ' ')}</option>)}
                </select>
                <span style={{ color: subtle, fontSize: '13px' }}>{filteredFeedback.length} result{filteredFeedback.length !== 1 ? 's' : ''}</span>
              </div>
            </Card>

            {/* Bulk Action Controls */}
            {selectedFeedback.size > 0 && (
              <Card isDark={isDarkTheme} style={{
                marginBottom: '16px',
                padding: '14px 20px',
                background: isDarkTheme ? '#0c2744' : '#f0f9ff',
                border: `1px solid ${isDarkTheme ? '#0369a1' : '#0ea5e9'}`,
              }}>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ fontWeight: '600', color: isDarkTheme ? '#bae6fd' : '#0c4a6e' }}>
                      {selectedFeedback.size} item{selectedFeedback.size > 1 ? 's' : ''} selected
                    </span>
                    <button
                      onClick={() => bulkUpdateStatus('completed')}
                      disabled={bulkActionLoading}
                      style={{
                        padding: '6px 12px',
                        background: '#10b981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: bulkActionLoading ? 'not-allowed' : 'pointer',
                        opacity: bulkActionLoading ? 0.6 : 1,
                      }}
                    >
                      {bulkActionLoading ? 'Processing...' : 'Mark Complete'}
                    </button>
                    <button
                      onClick={() => bulkUpdateStatus('in_progress')}
                      disabled={bulkActionLoading}
                      style={{
                        padding: '6px 12px',
                        background: '#3b82f6',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: bulkActionLoading ? 'not-allowed' : 'pointer',
                        opacity: bulkActionLoading ? 0.6 : 1,
                      }}
                    >
                      {bulkActionLoading ? 'Processing...' : 'In Progress'}
                    </button>
                    <button
                      onClick={() => bulkUpdateStatus('discarded')}
                      disabled={bulkActionLoading}
                      style={{
                        padding: '6px 12px',
                        background: '#6b7280',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: bulkActionLoading ? 'not-allowed' : 'pointer',
                        opacity: bulkActionLoading ? 0.6 : 1,
                      }}
                    >
                      {bulkActionLoading ? 'Processing...' : 'Discard'}
                    </button>
                    <button
                      onClick={bulkDeleteFeedback}
                      disabled={bulkActionLoading}
                      style={{
                        padding: '6px 12px',
                        background: '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: bulkActionLoading ? 'not-allowed' : 'pointer',
                        opacity: bulkActionLoading ? 0.6 : 1,
                      }}
                    >
                      {bulkActionLoading ? 'Processing...' : 'Delete'}
                    </button>
                  </div>
                  <button
                    onClick={() => setSelectedFeedback(new Set())}
                    style={{ ...buttonStyle('secondary', false, isDarkTheme), padding: '6px 12px', fontSize: '12px' }}
                  >
                    Clear Selection
                  </button>
                </div>
                {bulkActionMessage && (
                  <div style={{
                    marginTop: '10px',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background: bulkActionMessage.startsWith('✓')
                      ? (isDarkTheme ? '#052e16' : '#f0fdf4')
                      : (isDarkTheme ? '#3f1219' : '#fef2f2'),
                    color: bulkActionMessage.startsWith('✓')
                      ? (isDarkTheme ? '#86efac' : '#166534')
                      : (isDarkTheme ? '#fecaca' : '#991b1b'),
                    border: `1px solid ${bulkActionMessage.startsWith('✓') ? '#16a34a' : '#dc2626'}`,
                  }}>
                    {bulkActionMessage}
                  </div>
                )}
              </Card>
            )}

            {isMobile ? (
              <div>
                {filteredFeedback.length === 0 && (
                  <Card isDark={isDarkTheme}><p style={{ margin: 0, textAlign: 'center', color: '#94a3b8' }}>No feedback found.</p></Card>
                )}
                {filteredFeedback.map(item => (
                  <FeedbackCard
                    key={item.id}
                    item={item}
                    onView={setSelectedTicket}
                    editingFeedbackId={editingFeedbackId}
                    editingStatus={editingStatus}
                    setEditingStatus={setEditingStatus}
                    feedbackSaving={feedbackSaving}
                    saveFeedbackStatus={saveFeedbackStatus}
                    setEditingFeedbackId={setEditingFeedbackId}
                    setDeleteConfirmId={setDeleteConfirmId}
                    isDark={isDarkTheme}
                  />
                ))}
              </div>
            ) : (
              <Card isDark={isDarkTheme} style={{ padding: 0, overflow: 'visible' }}>
                <div style={tableScrollWrap}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', minWidth: 880 }}>
                  <thead>
                    <tr style={{ background: ownerHeaderBg, borderBottom: `1px solid ${ownerCardBorder}` }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', fontSize: '12px', color: ownerHeaderText, letterSpacing: '0.04em', width: '60px' }}>
                        <input
                          type="checkbox"
                          checked={selectedFeedback.size === filteredFeedback.length && filteredFeedback.length > 0}
                          onChange={toggleSelectAll}
                          style={{ cursor: 'pointer' }}
                        />
                      </th>
                      {['From', 'AI classification', 'Status', 'Subject & Message'].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', fontSize: '12px', color: ownerHeaderText, letterSpacing: '0.04em' }}>
                          {h.toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFeedback.length === 0 && (
                      <tr><td colSpan={5} style={{ padding: '32px', textAlign: 'center', color: ownerEmptyText }}>No feedback found.</td></tr>
                    )}
                    {filteredFeedback.map((item, i) => (
                      <tr key={item.id} style={{ borderBottom: `1px solid ${ownerRowBorder}`, background: i % 2 === 0 ? ownerRowEven : ownerRowOdd }}>
                        {/* SELECT CHECKBOX */}
                        <td style={{ padding: '12px 12px', textAlign: 'center', width: '60px', whiteSpace: 'nowrap' }}>
                          <input
                            type="checkbox"
                            checked={selectedFeedback.has(item.id)}
                            onChange={() => toggleFeedbackSelection(item.id)}
                            style={{ cursor: 'pointer' }}
                          />
                        </td>

                        {/* FROM */}
                        <td style={{ padding: '12px 16px', minWidth: '160px' }}>
                          <div style={{ fontWeight: '600', color: ownerNameText, fontSize: '13px' }}>{item.userName || 'Unknown'}</div>
                          <div style={{ color: ownerBodyText, fontSize: '11px' }}>{item.userEmail || '—'}</div>
                          <div style={{ color: subtle, fontSize: '11px' }}>{item.userRole || '—'}</div>
                        </td>

                        {/* AI CLASSIFICATION */}
                        <td style={{ padding: '12px 16px', minWidth: '160px', maxWidth: '220px' }}>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '4px' }}>
                            {(() => {
                              const cls = feedbackEffectiveClassification(item);
                              const pri = feedbackEffectivePriority(item);
                              const cc = classificationBadgeColors(cls);
                              const pc = priorityBadgeColors(pri);
                              return (
                                <>
                                  <Badge children={cls.replace(/_/g, ' ')} color={cc.color} bg={cc.bg} />
                                  <Badge children={pri} color={pc.color} bg={pc.bg} />
                                </>
                              );
                            })()}
                          </div>
                          {item.aiAnalysis?.summary ? (
                            <div
                              style={{
                                fontSize: '11px',
                                color: ownerBodyText,
                                lineHeight: 1.35,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                              title={item.aiAnalysis.summary}
                            >
                              {item.aiAnalysis.summary}
                            </div>
                          ) : feedbackAiPending(item) ? (
                            <div style={{ fontSize: '11px', color: '#ca8a04' }}>AI pending…</div>
                          ) : item.aiAnalysisError ? (
                            <div style={{ fontSize: '11px', color: '#dc2626' }}>AI error</div>
                          ) : null}
                        </td>

                        {/* STATUS (button that opens menu) */}
                        <td style={{ padding: '12px 16px', minWidth: '140px', position: 'relative' }}>
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button onClick={() => setStatusMenuOpen(statusMenuOpen === item.id ? null : item.id)}
                              style={{
                                padding: '6px 10px', borderRadius: '6px',
                                border: `2px solid ${feedbackStatusUi(item.status, isDarkTheme).color}`,
                                background: feedbackStatusUi(item.status, isDarkTheme).bg,
                                color: feedbackStatusUi(item.status, isDarkTheme).color,
                                fontWeight: '600', fontSize: '12px', cursor: 'pointer',
                                minWidth: '100px', textAlign: 'center', transition: 'all 0.2s',
                              }}>
                              {(item.status || 'new').replace(/_/, ' ')}
                            </button>
                            {statusMenuOpen === item.id && (
                              <div style={{
                                position: 'absolute', top: '100%', left: 0, marginTop: '4px',
                                background: isDarkTheme ? '#1e293b' : '#fff',
                                border: `1px solid ${isDarkTheme ? '#475569' : '#d1d5db'}`,
                                borderRadius: '7px',
                                boxShadow: isDarkTheme ? '0 4px 12px rgba(0,0,0,0.45)' : '0 4px 12px rgba(0,0,0,0.15)',
                                zIndex: 100, minWidth: '140px',
                              }}>
                                {FEEDBACK_STATUSES.map(s => (
                                  <button key={s} onClick={() => { saveFeedbackStatus(item.id, s); setStatusMenuOpen(null); }}
                                    style={{
                                      display: 'block', width: '100%', padding: '10px 12px', textAlign: 'left',
                                      background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px',
                                      color: isDarkTheme ? '#e2e8f0' : '#374151',
                                      borderBottom: `1px solid ${ownerCardBorder}`,
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = isDarkTheme ? '#334155' : '#f8fafc'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                                    {s.replace(/_/, ' ')}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: '11px', color: subtle, marginTop: '4px' }}>{formatDateTime(item.createdAt)}</div>
                        </td>

                        {/* SUBJECT & MESSAGE */}
                        <td style={{ padding: '12px 16px', minWidth: '280px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', color: ownerNameText, fontSize: '13px', marginBottom: '4px' }}>{item.subject || 'No subject'}</div>
                              <div style={{ color: ownerBodyText, fontSize: '12px', lineHeight: '1.4', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                {item.message || '—'}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                              <button onClick={() => setSelectedTicket(item)}
                                title="View details"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '4px', opacity: 0.6, transition: 'opacity 0.2s' }}
                                onMouseEnter={e => e.target.style.opacity = '1'}
                                onMouseLeave={e => e.target.style.opacity = '0.6'}>
                                📋
                              </button>
                              <button onClick={() => setDeleteConfirmId(item.id)}
                                title="Delete"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '4px', opacity: 0.6, transition: 'opacity 0.2s', color: '#dc2626' }}
                                onMouseEnter={e => e.target.style.opacity = '1'}
                                onMouseLeave={e => e.target.style.opacity = '0.6'}>
                                🗑️
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </Card>
            )}

            {selectedTicket && (
              <TicketDrawer
                ticket={selectedTicket}
                onClose={() => { setSelectedTicket(null); setTenantName(''); }}
                resolvedName={resolvedTicketName}
                onStatusSave={saveFeedbackStatus}
                onDelete={(id) => setDeleteConfirmId(id)}
                onSaveNotes={saveAdminNotes}
                onSaveWhatHasBeenDone={saveWhatHasBeenDone}
                isDark={isDarkTheme}
              />
            )}

            {deleteConfirmId && (
              <DeleteConfirmDialog
                onConfirm={() => deleteFeedback(deleteConfirmId)}
                onCancel={() => setDeleteConfirmId(null)}
                isDark={isDarkTheme}
              />
            )}
          </div>
        )}

        {/* ── PLATFORM SETTINGS ── */}
        {activeTab === 'settings' && (
          <div>
            <SectionTitle isMobile={isMobile} isDark={isDarkTheme} sub="Global configuration for the entire platform. Changes affect all users immediately.">
              Platform Settings
            </SectionTitle>
            <DashboardAppearanceSettings style={{ marginBottom: '20px' }} />

            {/* Platform service fee (stored in PlatformSettings/fees) */}
            <Card id="admin-platform-service-fee" isDark={isDarkTheme} style={{ marginBottom: '20px', maxWidth: isMobile ? 'none' : '640px', scrollMarginTop: '24px' }}>
              <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: '700', color: headingText }}>💰 Platform service fee</h3>
              <p style={{ margin: '0 0 20px', color: subtle, fontSize: '13px', lineHeight: 1.5 }}>
                Percentage deducted from the professional&apos;s payout when an estimate is accepted and paid.
                Set to <strong>0</strong> to charge no platform fee. Per-professional overrides are managed from each professional&apos;s detail panel.
              </p>

              <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', color: labelText, marginBottom: '6px' }}>
                Default fee (%)
              </label>
              <div style={{
                display: 'flex',
                alignItems: isMobile ? 'stretch' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '12px',
              }}>
                <div style={{ position: 'relative', width: isMobile ? '100%' : '160px', maxWidth: '100%' }}>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={feePercent}
                    onChange={e => setFeePercent(e.target.value)}
                    style={{ ...inputThemed, width: '100%', paddingRight: '32px' }}
                    placeholder="e.g. 10"
                  />
                  <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontWeight: 700 }}>%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={saveFee}
                    disabled={feeSaving}
                    style={{
                      padding: '9px 22px', background: feeSaving ? '#9ca3af' : PURPLE,
                      color: '#fff', border: 'none', borderRadius: '7px',
                      fontWeight: '700', fontSize: '14px', cursor: feeSaving ? 'not-allowed' : 'pointer',
                      width: isMobile ? '100%' : 'auto',
                    }}
                  >
                    {feeSaving ? 'Saving…' : 'Save fee'}
                  </button>
                  {feeSaved && <span style={{ color: isDarkTheme ? '#86efac' : '#16a34a', fontWeight: '600', fontSize: '14px' }}>✓ Saved!</span>}
                </div>
              </div>

              {feePercent !== '' && (
                <div style={{
                  marginTop: '18px',
                  background: isDarkTheme ? '#1e1b4b' : '#f5f3ff',
                  border: `1px solid ${isDarkTheme ? '#4338ca' : '#ddd6fe'}`,
                  borderRadius: '8px',
                  padding: '12px 16px',
                }}>
                  <p style={{ margin: 0, fontSize: isMobile ? '12px' : '13px', color: isDarkTheme ? '#c4b5fd' : PURPLE, fontWeight: 600, lineHeight: 1.5, wordBreak: 'break-word' }}>
                    Example: A $500 job → professional receives ${(500 * (1 - parseFloat(feePercent || 0) / 100)).toFixed(2)}, platform earns ${(500 * parseFloat(feePercent || 0) / 100).toFixed(2)}
                  </p>
                </div>
              )}

              <div style={{
                marginTop: '18px',
                padding: '12px 14px',
                borderRadius: '8px',
                borderLeft: `4px solid ${isDarkTheme ? '#d97706' : '#f59e0b'}`,
                background: isDarkTheme ? '#422006' : '#fffbeb',
              }}>
                <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: headingText, marginBottom: '8px' }}>Notes</p>
                <ul style={{ margin: 0, paddingLeft: '18px', color: subtle, fontSize: '13px', lineHeight: 1.75 }}>
                  <li>The fee is applied at payout time, not when the estimate is created.</li>
                  <li>Changes apply to new transactions; existing pending flows are not changed retroactively.</li>
                </ul>
              </div>
            </Card>

            {/* General */}
            <Card isDark={isDarkTheme} style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: '700', color: headingText }}>🏷️ General</h3>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', color: labelText, marginBottom: '6px' }}>Platform Name</label>
                <input type="text" value={settings.platformName}
                  onChange={e => setSettings(p => ({ ...p, platformName: e.target.value }))}
                  style={{ ...inputThemed, maxWidth: isMobile ? '100%' : '360px' }} />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', color: labelText, marginBottom: '6px' }}>Support Email</label>
                <input type="email" value={settings.supportEmail}
                  onChange={e => setSettings(p => ({ ...p, supportEmail: e.target.value }))}
                  placeholder="support@tenantdispatch.com"
                  style={{ ...inputThemed, maxWidth: isMobile ? '100%' : '360px' }} />
              </div>

              <p style={{ margin: 0, fontSize: '13px', color: subtle, lineHeight: 1.5 }}>
                <strong style={{ color: labelText }}>Max units per owner</strong> is configured per owner on the{' '}
                <button
                  type="button"
                  onClick={() => setActiveTab('owners')}
                  style={{ background: 'none', border: 'none', padding: 0, color: PURPLE, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Owners
                </button>{' '}
                tab (optional limit per account).
              </p>
            </Card>

            {/* Access control */}
            <Card isDark={isDarkTheme} style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: '700', color: headingText }}>🔐 Access Control</h3>
              {[
                { key: 'allowNewOwnerSignups',        label: 'Allow New Owner Signups',        desc: 'When off, new owners cannot create accounts' },
                { key: 'allowNewProfessionalSignups', label: 'Allow New Professional Signups', desc: 'When off, new professionals cannot create accounts' },
                { key: 'allowNewRealtorSignups',      label: 'Allow New Realtor Signups',      desc: 'When off, new realtors cannot create accounts' },
              ].map(({ key, label, desc }) => (
                <div key={key} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '10px' : '0',
                  padding: '14px 0',
                  borderBottom: `1px solid ${ownerRowBorder}`,
                }}>
                  <div style={{ width: isMobile ? '100%' : 'auto' }}>
                    <p style={{ margin: 0, fontWeight: '600', fontSize: '14px', color: headingText }}>{label}</p>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: subtle }}>{desc}</p>
                  </div>
                  <Toggle checked={!!settings[key]} onChange={e => setSettings(p => ({ ...p, [key]: e.target.checked }))} />
                </div>
              ))}
            </Card>

            {/* Admin Invitations */}
            <Card isDark={isDarkTheme} style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: '700', color: headingText }}>👥 Admin Invitations</h3>
              <p style={{ margin: '0 0 16px', color: subtle, fontSize: '13px', lineHeight: '1.5' }}>
                Invite someone to become a platform administrator. They'll receive an email with a secure link to set up their admin account.
              </p>
              
              <div style={{
                display: 'flex',
                gap: '12px',
                alignItems: isMobile ? 'stretch' : 'flex-end',
                flexDirection: isMobile ? 'column' : 'row',
                marginBottom: '16px',
              }}>
                <div style={{ flex: 1, maxWidth: isMobile ? 'none' : '400px', width: isMobile ? '100%' : 'auto' }}>
                  <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', color: labelText, marginBottom: '6px' }}>Email Address</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder="admin@example.com"
                    onKeyPress={e => e.key === 'Enter' && sendAdminInvite()}
                    style={{ ...inputThemed, width: '100%' }}
                  />
                </div>
                <button
                  onClick={sendAdminInvite}
                  disabled={inviteLoading || !inviteEmail}
                  style={{
                    padding: '10px 20px',
                    background: inviteLoading ? '#9ca3af' : PURPLE,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: inviteLoading || !inviteEmail ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    width: isMobile ? '100%' : 'auto',
                  }}
                >
                  {inviteLoading ? 'Sending...' : '📧 Send Invite'}
                </button>
              </div>
              
              {inviteMessage && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  background: inviteMessage.startsWith('✓')
                    ? (isDarkTheme ? '#052e16' : '#f0fdf4')
                    : (isDarkTheme ? '#3f1219' : '#fef2f2'),
                  color: inviteMessage.startsWith('✓')
                    ? (isDarkTheme ? '#86efac' : '#166534')
                    : (isDarkTheme ? '#fecaca' : '#991b1b'),
                  border: `1px solid ${inviteMessage.startsWith('✓') ? '#16a34a' : '#dc2626'}`,
                  wordBreak: 'break-word',
                }}>
                  {inviteMessage}
                </div>
              )}
              
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: isDarkTheme ? '#0f172a' : '#f8fafc',
                borderRadius: '6px',
                border: `1px solid ${ownerCardBorder}`,
              }}>
                <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '600', color: labelText }}>📋 How it works:</p>
                <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12px', color: subtle, lineHeight: '1.5' }}>
                  <li style={{ marginBottom: '4px' }}>Enter the email address of the person you want to make an admin</li>
                  <li style={{ marginBottom: '4px' }}>Click "Send Invite" to email them a secure invitation link</li>
                  <li style={{ marginBottom: '4px' }}>They'll receive an email with instructions to set up their admin account</li>
                  <li style={{ marginBottom: '4px' }}>Once they accept, they'll have full admin access to the platform</li>
                </ul>
              </div>

            </Card>

            {/* Feature flags */}
            <Card isDark={isDarkTheme} style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: '700', color: headingText }}>🚩 Feature Flags</h3>
              {[
                { key: 'featurePayments', label: 'Payments',  desc: 'Enable in-app estimate payments between owners and professionals' },
                { key: 'featureFeedback', label: 'Feedback',  desc: 'Show the feedback button to all logged-in users' },
              ].map(({ key, label, desc }) => (
                <div key={key} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '10px' : '0',
                  padding: '14px 0',
                  borderBottom: `1px solid ${ownerRowBorder}`,
                }}>
                  <div style={{ width: isMobile ? '100%' : 'auto' }}>
                    <p style={{ margin: 0, fontWeight: '600', fontSize: '14px', color: headingText }}>{label}</p>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: subtle }}>{desc}</p>
                  </div>
                  <Toggle checked={!!settings[key]} onChange={e => setSettings(p => ({ ...p, [key]: e.target.checked }))} />
                </div>
              ))}
            </Card>

            {/* Maintenance mode */}
            <Card isDark={isDarkTheme} style={{ marginBottom: '24px', borderLeft: '4px solid #dc2626' }}>
              <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: '700', color: '#dc2626' }}>🚨 Maintenance Mode</h3>
              <p style={{ margin: '0 0 16px', color: subtle, fontSize: '13px' }}>
                When on, all non-admin users will see a maintenance notice instead of their dashboard.
              </p>
              <div style={{
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '14px',
              }}>
                <Toggle checked={!!settings.maintenanceMode} onChange={e => setSettings(p => ({ ...p, maintenanceMode: e.target.checked }))} danger />
                <span style={{ fontWeight: '600', color: settings.maintenanceMode ? '#dc2626' : subtle, fontSize: '14px', lineHeight: 1.4 }}>
                  {settings.maintenanceMode ? '⚠️ Maintenance mode is ON' : 'Maintenance mode is off'}
                </span>
              </div>
            </Card>

            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <button
                onClick={saveSettings}
                disabled={settingsSaving}
                style={{
                  background: settingsSaving ? '#9ca3af' : PURPLE,
                  color: '#fff', border: 'none', borderRadius: '8px',
                  padding: '11px 28px', fontWeight: '700', fontSize: '14px',
                  cursor: settingsSaving ? 'not-allowed' : 'pointer',
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                {settingsSaving ? 'Saving…' : '💾 Save Settings'}
              </button>
              {settingsSaved && <span style={{ color: isDarkTheme ? '#86efac' : '#16a34a', fontWeight: '600', fontSize: '14px' }}>✓ Saved!</span>}
            </div>
          </div>
        )}

        {mobileRecordDetail && (
          <MobileRecordDrawer
            title={
              mobileRecordDetail.kind === 'owner'
                ? (mobileRecordDetail.item.name || 'Owner')
                : mobileRecordDetail.kind === 'professional'
                  ? (mobileRecordDetail.item.fullName || mobileRecordDetail.item.name || mobileRecordDetail.item.businessName || mobileRecordDetail.item.email || 'Professional')
                  : (mobileRecordDetail.item.name || 'Realtor')
            }
            onClose={() => setMobileRecordDetail(null)}
            isDark={isDarkTheme}
            fullWidthSheet={isMobile}
          >
            {mobileRecordDetail.kind === 'owner' && (() => {
              const o = mobileRecordDetail.item;
              return (
                <>
                  <div style={{ fontWeight: '800', color: headingText, fontSize: '16px', marginBottom: '6px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                    <span>{o.name || '—'}</span>
                    {o.id === user.uid && <Badge children="You" color="#2563eb" bg="#eff6ff" />}
                  </div>
                  <div style={{ fontSize: '13px', color: ownerBodyText, wordBreak: 'break-word', marginBottom: '8px' }}>{o.email || '—'}</div>
                  <div style={{ fontSize: '13px', color: ownerBodyText, marginBottom: '4px' }}><strong>Phone:</strong> {o.phone || '—'}</div>
                  <div style={{ fontSize: '13px', color: ownerBodyText, marginBottom: '8px' }}><strong>Joined:</strong> {formatDateTime(o.createdAt)}</div>
                  <div>
                    {o.isSuperAdmin
                      ? <Badge children="Super Admin" color={PURPLE} bg="#f5f3ff" />
                      : <span style={{ color: '#94a3b8', fontSize: '13px' }}>Not super admin</span>}
                  </div>
                  <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: `1px solid ${ownerRowBorder}` }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.04em', marginBottom: '8px' }}>MAX UNITS (OPTIONAL)</div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="number"
                        min={1}
                        placeholder="No limit"
                        value={ownerMaxUnitsInputs[o.id] ?? ''}
                        onChange={(e) => setOwnerMaxUnitsInputs((prev) => ({ ...prev, [o.id]: e.target.value }))}
                        style={{ ...inputThemed, flex: 1, minWidth: '100px', padding: '8px 10px', fontSize: '14px' }}
                      />
                      <button
                        type="button"
                        onClick={() => saveOwnerMaxUnits(o.id)}
                        disabled={ownerMaxUnitsSavingId === o.id}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '7px',
                          border: `1px solid ${PURPLE}55`,
                          background: '#f5f3ff',
                          color: PURPLE,
                          fontWeight: '700',
                          fontSize: '13px',
                          cursor: ownerMaxUnitsSavingId === o.id ? 'wait' : 'pointer',
                        }}
                      >
                        {ownerMaxUnitsSavingId === o.id ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                    <p style={{ margin: '8px 0 0', fontSize: '12px', color: subtle, lineHeight: 1.45 }}>
                      Leave blank for no cap. (Subscription-based limits can be layered on later.)
                    </p>
                  </div>
                  <div style={{ marginTop: '16px' }}>
                    <button
                      type="button"
                      onClick={() => handleAdminDeleteUser(o.id, 'OwnersData', o.name || o.email || o.id)}
                      disabled={!!adminUserDeleteBusy || o.id === user.uid || o.isSuperAdmin === true}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '7px',
                        border: '1px solid #fecaca',
                        background: '#fff1f2',
                        color: '#b91c1c',
                        fontWeight: '700',
                        fontSize: '13px',
                        cursor: adminUserDeleteBusy || o.id === user.uid || o.isSuperAdmin ? 'not-allowed' : 'pointer',
                        opacity: o.id === user.uid || o.isSuperAdmin ? 0.5 : 1,
                      }}
                    >
                      Delete owner account
                    </button>
                  </div>
                </>
              );
            })()}
            {mobileRecordDetail.kind === 'professional' && (() => {
              const stale = mobileRecordDetail.item;
              const p = professionals.find((x) => x.id === stale.id) || stale;
              return (
                <div style={{ display: 'grid', gap: '10px', fontSize: '13px' }}>
                  <div style={{ fontWeight: '800', color: p.stripeConnectId ? (isDarkTheme ? '#86efac' : '#166534') : headingText, fontSize: '16px' }}>
                    {getProfessionalDisplayName(p)}
                  </div>
                  <div style={{ color: ownerBodyText, wordBreak: 'break-word' }}>{p.email || '—'}</div>
                  <div>
                    <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.04em' }}>SPECIALTY</span>
                    <div style={{ marginTop: '4px' }}>
                      {getProfessionalCategories(p).length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {getProfessionalCategories(p).map(cat => (
                            <Badge key={`drawer-${p.id}-${cat}`} children={cat} color="#0f766e" bg="#f0fdfa" />
                          ))}
                        </div>
                      ) : <span style={{ color: '#94a3b8' }}>—</span>}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.04em' }}>BUSINESS</span>
                    <div style={{ marginTop: '4px', color: headingText }}>{p.businessName || '—'}</div>
                  </div>
                  <div>
                    <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '700' }}>JOINED</span>
                    <div style={{ marginTop: '4px', color: ownerBodyText }}>{formatDateTime(p.createdAt)}</div>
                  </div>
                  <div>
                    <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.04em' }}>PRICING</span>
                    <div style={{ marginTop: '4px', color: ownerBodyText, lineHeight: 1.45 }}>{getProfessionalRateLabel(p)}</div>
                  </div>

                  <div style={{ paddingTop: '8px', borderTop: `1px solid ${ownerRowBorder}` }}>
                    <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.04em' }}>ONBOARDING DEPOSIT ($20)</span>
                    <div style={{ marginTop: '8px', display: 'grid', gap: '10px' }}>
                      <div style={{ color: ownerBodyText, lineHeight: 1.5 }}>
                        {p.depositPaid && p.adminLeadAccessGranted ? (
                          <span>
                            <strong style={{ color: headingText }}>Waived by admin</strong> — full leads without Stripe deposit.
                          </span>
                        ) : p.depositPaid ? (
                          <span>
                            <strong style={{ color: headingText }}>Satisfied</strong> — paid through Stripe (admin cannot remove here).
                          </span>
                        ) : (
                          <span>
                            <strong style={{ color: headingText }}>Not paid</strong> — job board stays locked until they pay or you waive.
                          </span>
                        )}
                      </div>
                      {!p.depositPaid ? (
                        <button
                          type="button"
                          onClick={() => handleGrantProDepositWaiver(p)}
                          disabled={proDepositWaiverBusyId === p.id}
                          style={{
                            padding: '10px 14px',
                            borderRadius: '7px',
                            border: `1px solid ${isDarkTheme ? '#6d28d9' : '#c4b5fd'}`,
                            background: proDepositWaiverBusyId === p.id ? (isDarkTheme ? '#312e81' : '#ede9fe') : (isDarkTheme ? '#3730a3' : '#f5f3ff'),
                            color: isDarkTheme ? '#e9d5ff' : '#5b21b6',
                            fontWeight: '700',
                            fontSize: '13px',
                            cursor: proDepositWaiverBusyId === p.id ? 'wait' : 'pointer',
                          }}
                        >
                          {proDepositWaiverBusyId === p.id ? 'Applying…' : 'Waive deposit requirement'}
                        </button>
                      ) : null}
                      {p.depositPaid && p.adminLeadAccessGranted ? (
                        <button
                          type="button"
                          onClick={() => handleRevokeProDepositWaiver(p)}
                          disabled={proDepositWaiverBusyId === p.id}
                          style={{
                            padding: '10px 14px',
                            borderRadius: '7px',
                            border: '1px solid #fecaca',
                            background: '#fff1f2',
                            color: '#b91c1c',
                            fontWeight: '700',
                            fontSize: '13px',
                            cursor: proDepositWaiverBusyId === p.id ? 'wait' : 'pointer',
                          }}
                        >
                          {proDepositWaiverBusyId === p.id ? 'Updating…' : 'Remove admin waiver'}
                        </button>
                      ) : null}
                      {proDepositWaiverMessage ? (
                        <div
                          style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: proDepositWaiverMessage.startsWith('✓')
                              ? (isDarkTheme ? '#86efac' : '#166534')
                              : (isDarkTheme ? '#fca5a5' : '#b91c1c'),
                          }}
                        >
                          {proDepositWaiverMessage}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div style={{ paddingTop: '8px', borderTop: `1px solid ${ownerRowBorder}` }}>
                    <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.04em' }}>PAYOUT ACCOUNT</span>
                    <div style={{ marginTop: '8px' }}>
                      {p.stripeConnectId ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <Badge children="Connected" color={isDarkTheme ? '#86efac' : '#166534'} bg={isDarkTheme ? '#052e16' : '#f0fdf4'} />
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                            <button
                              type="button"
                              onClick={() => handleDisconnectProfessionalStripe(p)}
                              disabled={disconnectingStripeFor === p.id}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #fecaca',
                                background: '#fff1f2',
                                color: '#b91c1c',
                                fontSize: '13px',
                                fontWeight: '700',
                                cursor: disconnectingStripeFor === p.id ? 'not-allowed' : 'pointer',
                                opacity: disconnectingStripeFor === p.id ? 0.7 : 1,
                              }}
                            >
                              {disconnectingStripeFor === p.id ? 'Disconnecting...' : 'Disconnect Stripe link'}
                            </button>
                          </div>
                          <div style={{ fontSize: '11px', color: ownerBodyText, fontFamily: 'monospace', wordBreak: 'break-all', padding: '8px', background: isDarkTheme ? '#0f172a' : '#f9fafb', borderRadius: '6px', border: `1px solid ${ownerRowBorder}` }}>
                            {p.stripeConnectId}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: ownerBodyText, fontSize: '13px' }}>Not connected — they can link Stripe from their professional dashboard.</span>
                      )}
                    </div>
                  </div>

                  <div style={{ paddingTop: '8px', borderTop: `1px solid ${ownerRowBorder}` }}>
                    <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.04em' }}>
                      FEE OVERRIDE
                    </span>
                    <div style={{ marginTop: '10px', display: 'grid', gap: '10px' }}>
                      <div style={{ color: ownerBodyText, lineHeight: 1.45 }}>
                        <span>Platform default: </span>
                        <strong>{feePercent !== '' ? `${feePercent}%` : 'Not set'}</strong>
                        {feePercent === '' ? (
                          <span style={{ display: 'block', fontSize: '12px', color: subtle, fontWeight: 500, marginTop: '4px' }}>
                            Set the default under Platform Settings → Platform service fee.
                          </span>
                        ) : null}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        <div style={{ color: headingText, fontWeight: 700, fontSize: 13 }}>Override this professional’s fee</div>
                        <Toggle checked={proFeeOverrideEnabled} onChange={(e) => setProFeeOverrideEnabled(e.target.checked)} />
                      </div>

                      {proFeeOverrideEnabled ? (
                        <div>
                          <label style={{ display: 'block', fontWeight: '700', fontSize: 12, color: labelText, marginBottom: 6 }}>
                            Fee percentage (%)
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={proFeeOverrideValue}
                            onChange={(e) => setProFeeOverrideValue(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '9px 12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '7px',
                              fontSize: '14px',
                              outline: 'none',
                              boxSizing: 'border-box'
                            }}
                            placeholder="e.g. 12.5"
                          />
                        </div>
                      ) : (
                        <div style={{ color: subtle, fontSize: 12 }}>
                          Using platform default fee for this professional.
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={handleSaveProfessionalFeeOverride}
                        disabled={proFeeOverrideSaving}
                        style={{
                          padding: '10px 14px',
                          background: proFeeOverrideSaving ? '#e5e7eb' : PURPLE,
                          color: '#fff',
                          border: 'none',
                          borderRadius: '7px',
                          fontWeight: '700',
                          cursor: proFeeOverrideSaving ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {proFeeOverrideSaving ? 'Saving…' : 'Save Fee Override'}
                      </button>

                      {proFeeOverrideMessage ? (
                        <div style={{ color: proFeeOverrideMessage.startsWith('✓') ? '#16a34a' : '#dc2626', fontWeight: 700, fontSize: 13 }}>
                          {proFeeOverrideMessage}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: `1px solid ${ownerRowBorder}` }}>
                    <button
                      type="button"
                      onClick={() => handleAdminDeleteUser(p.id, 'ProfessionalsData', getProfessionalDisplayName(p) || p.email || p.id)}
                      disabled={!!adminUserDeleteBusy || p.id === user.uid}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '7px',
                        border: '1px solid #fecaca',
                        background: '#fff1f2',
                        color: '#b91c1c',
                        fontWeight: '700',
                        fontSize: '13px',
                        cursor: adminUserDeleteBusy || p.id === user.uid ? 'not-allowed' : 'pointer',
                        opacity: p.id === user.uid ? 0.5 : 1,
                      }}
                    >
                      Delete professional account
                    </button>
                  </div>
                </div>
              );
            })()}
            {mobileRecordDetail.kind === 'realtor' && (() => {
              const r = mobileRecordDetail.item;
              return (
                <>
                  <div style={{ fontWeight: '800', color: headingText, fontSize: '16px', marginBottom: '6px' }}>{r.name || '—'}</div>
                  <div style={{ fontSize: '13px', color: ownerBodyText, wordBreak: 'break-word', marginBottom: '8px' }}>{r.email || '—'}</div>
                  <div style={{ fontSize: '13px', color: ownerBodyText, marginBottom: '4px' }}><strong>Phone:</strong> {r.phone || '—'}</div>
                  <div style={{ fontSize: '13px', color: ownerBodyText, marginBottom: '4px' }}><strong>Joined:</strong> {formatDateTime(r.createdAt)}</div>
                  <div style={{ fontSize: '13px', color: ownerBodyText, marginBottom: '4px' }}><strong>License:</strong> {r.licenseNumber || '—'}</div>
                  <div style={{ fontSize: '13px', color: ownerBodyText }}><strong>Agency:</strong> {r.agencyName || r.agency || '—'}</div>
                  <div style={{ marginTop: '16px' }}>
                    <button
                      type="button"
                      onClick={() => handleAdminDeleteUser(r.id, 'RealtorsData', r.name || r.email || r.id)}
                      disabled={!!adminUserDeleteBusy || r.id === user.uid}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '7px',
                        border: '1px solid #fecaca',
                        background: '#fff1f2',
                        color: '#b91c1c',
                        fontWeight: '700',
                        fontSize: '13px',
                        cursor: adminUserDeleteBusy || r.id === user.uid ? 'not-allowed' : 'pointer',
                        opacity: r.id === user.uid ? 0.5 : 1,
                      }}
                    >
                      Delete realtor account
                    </button>
                  </div>
                </>
              );
            })()}
          </MobileRecordDrawer>
        )}

      </main>
    </div>
  );
}

export default AdminDashboard;
